<template>
    <div id="page-landing" data-page="landing" class="landing-page-container">
        <!-- Top Navigation -->
        <header class="landing-header py-2 py-md-3 border-bottom" :class="{ 'landing-header-scrolled': isScrolled }">
            <div class="container d-flex align-items-center justify-content-between px-2 px-sm-3">
                <a
                    href="#page-landing"
                    role="button"
                    aria-label="Back to top"
                    title="Back to top"
                    data-test="btn-brand-scroll-top"
                    class="d-flex align-items-center brand-wrap brand-scroll-top-btn text-decoration-none"
                    @click.prevent="scrollToTop"
                >
                    <img :src="logoUrl" alt="VelocyTrack Logo" class="brand-logo-img mr-2" />
                    <span class="font-weight-bold brand-name text-white d-none d-lg-inline">VelocyTrack</span>
                    <span class="badge badge-primary ml-2 px-2 py-1 beta-pill d-none d-lg-inline">BETA</span>
                </a>
                <nav class="d-none d-lg-flex align-items-center landing-nav-links mx-3" aria-label="Main Navigation">
                    <a href="#capabilities" @click.prevent="scrollToSection('capabilities')" class="landing-nav-link" :class="{ 'active': activeSection === 'capabilities' }" data-test="nav-link-capabilities">Capabilities</a>
                    <a href="#compare" @click.prevent="scrollToSection('compare')" class="landing-nav-link" :class="{ 'active': activeSection === 'compare' }" data-test="nav-link-compare">Comparison</a>
                    <a href="#testimonials" @click.prevent="scrollToSection('testimonials')" class="landing-nav-link" :class="{ 'active': activeSection === 'testimonials' }" data-test="nav-link-testimonials">Reviews</a>
                    <a href="#security" @click.prevent="scrollToSection('security')" class="landing-nav-link" :class="{ 'active': activeSection === 'security' }" data-test="nav-link-security">Security</a>
                    <a href="#pricing" @click.prevent="scrollToSection('pricing')" class="landing-nav-link" :class="{ 'active': activeSection === 'pricing' }" data-test="nav-link-pricing">Pricing</a>
                    <a href="#faq" @click.prevent="scrollToSection('faq')" class="landing-nav-link" :class="{ 'active': activeSection === 'faq' }" data-test="nav-link-faq">FAQ</a>
                </nav>
                <div class="d-flex align-items-center header-actions ml-2">
                    <b-dropdown
                        data-mobile-nav="true"
                        size="sm"
                        variant="outline-info"
                        class="d-lg-none mr-1 header-mobile-jump-dropdown"
                        no-caret
                        right
                        data-test="dropdown-mobile-sections"
                        aria-label="Jump to section"
                    >
                        <template #button-content>
                            <i class="bi bi-list-nested mr-1"></i>
                            <span class="small font-weight-bold">Menu</span>
                            <i class="bi bi-chevron-down ml-1 extra-small"></i>
                        </template>
                        <b-dropdown-item @click="scrollToSection('capabilities')">Capabilities</b-dropdown-item>
                        <b-dropdown-item @click="scrollToSection('compare')">Comparison</b-dropdown-item>
                        <b-dropdown-item @click="scrollToSection('testimonials')">Reviews</b-dropdown-item>
                        <b-dropdown-item @click="scrollToSection('security')">Security</b-dropdown-item>
                        <b-dropdown-item @click="scrollToSection('pricing')">Pricing</b-dropdown-item>
                        <b-dropdown-item @click="scrollToSection('faq')">FAQ</b-dropdown-item>
                    </b-dropdown>
                    <b-button
                        size="sm"
                        data-test="btn-header-official-launch"
                        class="btn-aceternity-secondary mr-1 mr-sm-2 font-weight-bold d-inline-flex align-items-center px-2 px-sm-3 header-btn"
                        @click="scrollToLaunch"
                    >
                        <i class="bi bi-bell-fill mr-1 text-warning"></i>
                        <span class="d-none d-md-inline">Get Launch Updates</span>
                        <span class="d-inline d-md-none">Updates</span>
                    </b-button>
                    <div class="signin-compact" v-if="showSignIn">
                        <router-link to="/sign-in" data-test="btn-sign-in" class="btn btn-sm btn-outline-light font-weight-bold ml-1 ml-sm-2 px-2 px-sm-3 header-btn text-nowrap">Sign In</router-link>
                    </div>
                </div>
            </div>
        </header>

        <!-- Hero Section -->
        <section class="landing-hero py-5 text-center">
            <div class="container position-relative hero-content-layer">
                <!-- Launch Gate Badge -->
                <div class="d-inline-flex align-items-center aceternity-pill mb-4 px-3 py-1">
                    <span class="pulse-dot mr-2"></span>
                    <span class="small font-weight-bold">
                        <span v-if="!waitlistStatus.isOpen" class="text-warning">Beta Cohort at Capacity &bull; Launch Waitlist Open</span>
                        <span v-else class="text-gradient-cyan">Open Beta &bull; Instant Access for Azure DevOps Developers &bull; Limited Cohort Seats</span>
                    </span>
                </div>

                <h1 class="hero-title font-weight-bolder mb-3">
                    <span class="hero-title-main">Your Personal Azure DevOps Cockpit &amp; Time Tracker</span> <br class="d-none d-lg-inline" />
                    <span class="text-gradient-cyan">Built for Developers &amp; Team Leaders.</span>
                </h1>
                <p class="lead hero-subtitle mx-auto mb-4">
                    Log time against your real work items in seconds and see your sprint, pull requests, branches, and discussions in one focused view, without digging through Azure DevOps menus. Free forever for solo developers.
                </p>

                <!-- Hero Social Proof Strip -->
                <div class="d-flex justify-content-center mb-4">
                    <social-proof-strip :social-proof="waitlistStatus.socialProof"></social-proof-strip>
                </div>

                <div class="d-flex flex-column flex-sm-row flex-wrap justify-content-center align-items-center gap-3 mb-2 hero-cta-group">
                    <b-button
                        size="lg"
                        data-test="btn-hero-launch-demo"
                        :data-state="isLaunchingInstantDemo ? 'launching' : 'idle'"
                        class="btn-aceternity-secondary px-4 py-2 font-weight-bold mb-2 mb-sm-0 mr-sm-3 shadow-sm hero-cta-btn"
                        :disabled="isLaunchingInstantDemo || isLaunchingDemo"
                        @click="launchInstantDemo"
                    >
                        <b-spinner v-if="isLaunchingInstantDemo" small class="mr-2"></b-spinner>
                        <i v-else class="bi bi-terminal-fill mr-2 text-info"></i> Interactive Demo
                    </b-button>
                    <button
                        type="button"
                        data-test="btn-hero-video-tour"
                        class="hero-video-frame-btn mb-2 mb-sm-0 mr-sm-3 shadow-sm hero-cta-btn"
                        aria-label="Watch Product Demo"
                        title="Watch Product Demo"
                        @click="openVideoModal"
                    >
                        <span class="video-frame-thumb" aria-hidden="true">
                            <span class="video-frame-window-dots">
                                <span></span><span></span><span></span>
                            </span>
                            <span class="video-frame-play-circle">
                                <i class="bi bi-play-fill"></i>
                            </span>
                            <span class="video-frame-scrubber">
                                <span class="video-frame-scrubber-fill"></span>
                            </span>
                        </span>
                        <span class="video-frame-info text-left">
                            <span class="video-frame-title">Watch Product Demo</span>
                            <span class="video-frame-subtitle">
                                <i class="bi bi-volume-up-fill text-info mr-1"></i>HD Video &bull; {{ demoVideoDurationLabel }}
                            </span>
                        </span>
                    </button>
                    <b-button
                        size="lg"
                        data-test="btn-hero-request-beta"
                        class="btn-aceternity-primary px-4 py-2 font-weight-bold mb-2 mb-sm-0 shadow-lg hero-cta-btn"
                        @click="scrollToWaitlist"
                    >
                        <i class="bi bi-rocket-takeoff-fill mr-2"></i> Claim Beta
                    </b-button>
                </div>
                <div class="mb-4">
                    <small class="text-light-muted">
                        No credit card or setup needed &bull;
                        <a href="#" data-test="link-customize-demo-persona" @click.prevent="startInteractiveDemo" class="text-info text-decoration-none persona-custom-link">
                            Customize demo persona &rarr;
                        </a>
                    </small>
                    <div class="mt-2">
                        <small class="text-light-muted">
                            Interested in the official public launch?
                            <a href="#launch-notify" @click.prevent="scrollToLaunch" data-test="link-hero-launch-notify" class="text-gradient-cyan font-weight-bold ml-1 text-decoration-none hover-underline">
                                Get notified &rarr;
                            </a>
                            <span class="mx-2 text-muted">&bull;</span>
                            Have a beta invite?
                            <router-link to="/sign-in" data-test="link-hero-beta-signin" class="text-gradient-cyan font-weight-bold ml-1 text-decoration-none hover-underline">
                                Sign in here &rarr;
                            </router-link>
                        </small>
                    </div>
                </div>

                <!-- Platform Integration Badges -->
                <div class="d-flex flex-wrap justify-content-center align-items-center text-muted small mb-4">
                    <span class="mr-3 text-light-muted">Supported Platforms:</span>
                    <span class="aceternity-chip-badge mr-2 py-1 px-3 mb-1">
                        <i class="icon-azure mr-1"></i> Azure DevOps (Live Beta)
                    </span>
                    <span class="aceternity-chip-badge mr-2 py-1 px-3 mb-1 opacity-80">
                        <i class="fa-brands fa-github mr-1 text-white"></i> GitHub (Coming Next)
                    </span>
                    <span class="aceternity-chip-badge mr-2 py-1 px-3 mb-1 opacity-80">
                        <i class="fa-brands fa-gitlab text-warning mr-1"></i> GitLab (Coming Next)
                    </span>
                    <span class="aceternity-chip-badge py-1 px-3 mb-1 opacity-80">
                        <i class="fa-brands fa-jira text-primary mr-1"></i> Jira (Coming Next)
                    </span>
                </div>

                <!-- Hero Visual Cockpit Preview Frame -->
                <div class="hero-cockpit-wrapper mx-auto mt-4 text-left">
                    <div class="cockpit-window-frame shadow-2xl">
                        <!-- Window Titlebar -->
                        <div class="cockpit-titlebar d-flex align-items-center justify-content-between px-2 px-sm-3 py-2">
                            <div class="d-flex align-items-center flex-shrink-0 mr-2">
                                <span class="window-dot dot-red mr-1"></span>
                                <span class="window-dot dot-yellow mr-1"></span>
                                <span class="window-dot dot-green mr-2 mr-sm-3"></span>
                                <span class="small font-weight-bold text-light-muted d-none d-md-inline">
                                    <i class="bi bi-speedometer2 text-info mr-1"></i> VelocyTrack Cockpit &bull; Live Preview
                                </span>
                            </div>
                            <!-- View Selector Tabs (Horizontally scrollable on mobile) -->
                            <div class="cockpit-tab-pills d-flex">
                                <button
                                    type="button"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'timelog' }"
                                    @click="activeHeroTab = 'timelog'"
                                >
                                    <i class="bi bi-clock-history mr-1"></i> Timelog
                                </button>
                                <button
                                    type="button"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'backlog' }"
                                    @click="activeHeroTab = 'backlog'"
                                >
                                    <i class="bi bi-kanban mr-1"></i> Backlog
                                </button>
                                <button
                                    type="button"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'pull-requests' }"
                                    @click="activeHeroTab = 'pull-requests'"
                                >
                                    <i class="icon-azure-pr mr-1"></i> <span class="d-none d-sm-inline">Pull Requests</span><span class="d-inline d-sm-none">PRs</span>
                                </button>
                                <button
                                    type="button"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'branches' }"
                                    @click="activeHeroTab = 'branches'"
                                >
                                    <i class="icon-azure-branch mr-1"></i> Branches
                                </button>
                                <button
                                    type="button"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'discussions' }"
                                    @click="activeHeroTab = 'discussions'"
                                >
                                    <i class="bi bi-chat-left-text mr-1"></i> Discussions
                                </button>
                                <button
                                    type="button"
                                    data-test="tab-hero-video"
                                    class="cockpit-tab-pill"
                                    :class="{ active: activeHeroTab === 'video' }"
                                    @click="activeHeroTab = 'video'"
                                >
                                    <i class="bi bi-play-btn-fill mr-1 text-info"></i> <span class="d-none d-sm-inline">Video Tour</span><span class="d-inline d-sm-none">Video</span>
                                </button>
                            </div>
                        </div>

                        <!-- Cockpit Body: Timelog Tab -->
                        <div v-if="activeHeroTab === 'timelog'" class="cockpit-body p-0 position-relative">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame">
                                    <img :src="previewTimelog" alt="VelocyTrack Timelog Cockpit with Calendar and Breakdown Hours" class="cockpit-screenshot" />
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-primary-subtle text-primary mr-2"><i class="bi bi-pie-chart-fill mr-1"></i>156h Effort Distribution</span>
                                        <span class="badge badge-pill badge-success-subtle text-success mr-2"><i class="bi bi-calendar3 mr-1"></i>Interactive Monthly Calendar</span>
                                        <span class="badge badge-pill badge-info-subtle text-info"><i class="bi bi-speedometer mr-1"></i>Real-Time Target Progress</span>
                                    </div>
                                    <span class="small text-light-muted">Live Engineering Cockpit</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Body: Backlog Tab -->
                        <div v-else-if="activeHeroTab === 'backlog'" class="cockpit-body p-0 position-relative">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame">
                                    <img :src="previewBacklog" alt="VelocyTrack Sprint Backlog & Open Capacity" class="cockpit-screenshot" />
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-info-subtle text-info mr-2"><i class="bi bi-graph-up-arrow mr-1"></i>41.0h Remaining Capacity</span>
                                        <span class="badge badge-pill badge-warning-subtle text-warning mr-2"><i class="bi bi-kanban mr-1"></i>Sprint 42 Scope</span>
                                        <span class="badge badge-pill badge-success-subtle text-success"><i class="bi bi-check2-circle mr-1"></i>Real-Time Over-Allocation Flags</span>
                                    </div>
                                    <span class="small text-light-muted">Sprint Backlog View</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Body: Pull Requests Tab -->
                        <div v-else-if="activeHeroTab === 'pull-requests'" class="cockpit-body p-0 position-relative">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame">
                                    <img :src="previewPullRequests" alt="VelocyTrack Multi-Repo Pull Requests & Review Radar" class="cockpit-screenshot" />
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-primary-subtle text-primary mr-2"><i class="bi bi-git mr-1"></i>Multi-Repo Grouping</span>
                                        <span class="badge badge-pill badge-success-subtle text-success mr-2"><i class="bi bi-person-check-fill mr-1"></i>Reviewer Approvals & Flags</span>
                                        <span class="badge badge-pill badge-info-subtle text-info"><i class="bi bi-link-45deg mr-1"></i>Linked DevOps Work Items</span>
                                    </div>
                                    <span class="small text-light-muted">Multi-Repo PR Radar</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Body: Branches Tab -->
                        <div v-else-if="activeHeroTab === 'branches'" class="cockpit-body p-0 position-relative">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame">
                                    <img :src="previewBranches" alt="VelocyTrack Multi-Repo Branches & Ahead/Behind Tracking" class="cockpit-screenshot" />
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-success-subtle text-success mr-2"><i class="bi bi-arrow-up-circle mr-1"></i>Ahead & Behind Commit Diffs</span>
                                        <span class="badge badge-pill badge-secondary-subtle text-light-muted mr-2"><i class="bi bi-person-fill mr-1"></i>Author & Contributor Tracking</span>
                                        <span class="badge badge-pill badge-warning-subtle text-warning"><i class="bi bi-funnel mr-1"></i>Sprint & Status Filtering</span>
                                    </div>
                                    <span class="small text-light-muted">Active Branches View</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Body: Video Tour Tab -->
                        <div v-else-if="activeHeroTab === 'video'" class="cockpit-body p-0 position-relative" data-test="cockpit-tab-video-body">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame bg-dark">
                                    <video
                                        :src="demoVideoUrl"
                                        :poster="previewTimelog"
                                        controls
                                        preload="metadata"
                                        class="w-100 d-block"
                                        style="max-height: 620px; background: #020617;"
                                        data-test="video-inline-cockpit"
                                    ></video>
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-info-subtle text-info mr-2"><i class="bi bi-soundwave mr-1"></i>Narrated 9-Chapter Walkthrough</span>
                                        <span class="badge badge-pill badge-primary-subtle text-primary mr-2"><i class="bi bi-badge-cc-fill mr-1"></i>Synced Captions</span>
                                        <span class="badge badge-pill badge-success-subtle text-success"><i class="bi bi-check2-circle mr-1"></i>End-to-End Developer Storyline</span>
                                    </div>
                                    <a :href="demoVideoUrl" target="_blank" rel="noopener noreferrer" class="small text-info font-weight-bold text-decoration-none" data-test="link-cockpit-open-mp4">
                                        Open direct MP4 &nearr;
                                    </a>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Body: Discussions Tab -->
                        <div v-else class="cockpit-body p-0 position-relative">
                            <div class="cockpit-screenshot-wrapper">
                                <div class="cockpit-screenshot-frame">
                                    <img :src="previewDiscussions" alt="VelocyTrack Discussions & Mention Signals" class="cockpit-screenshot" />
                                </div>
                                <div class="cockpit-screenshot-badge-bar d-none d-md-flex align-items-center justify-content-between px-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <span class="badge badge-pill badge-danger-subtle text-danger mr-2"><i class="bi bi-exclamation-circle-fill mr-1"></i>Awaiting Answer Detection</span>
                                        <span class="badge badge-pill badge-warning-subtle text-warning mr-2"><i class="bi bi-at mr-1"></i>DevOps @Mentions Feed</span>
                                        <span class="badge badge-pill badge-primary-subtle text-primary"><i class="bi bi-chat-left-dots-fill mr-1"></i>Inline Work Item Threads</span>
                                    </div>
                                    <span class="small text-light-muted">Work Item Discussions View</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cockpit Footer Ribbon -->
                        <div class="cockpit-footer-ribbon d-flex flex-wrap align-items-center justify-content-between px-3 py-2">
                            <div class="small text-light-muted d-flex align-items-center">
                                <span class="pulse-dot mr-2"></span>
                                <span>Real-time Azure DevOps sync &bull; Zero manual timesheet friction</span>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button
                                    size="sm"
                                    variant="link"
                                    data-test="btn-cockpit-watch-video"
                                    class="text-gradient-cyan font-weight-bold p-0 mr-3 text-decoration-none small"
                                    @click="openVideoModal"
                                >
                                    <i class="bi bi-play-btn-fill mr-1"></i>Watch {{ demoVideoDurationShort }} video tour
                                </b-button>
                                <b-button
                                    size="sm"
                                    variant="link"
                                    class="text-info font-weight-bold p-0 text-decoration-none small"
                                    :disabled="isLaunchingInstantDemo || isLaunchingDemo"
                                    @click="launchInstantDemo"
                                >
                                    Explore in live sandbox &rarr;
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Core Platform Capabilities Section -->
        <section class="py-5 landing-capabilities-section" id="capabilities">
            <div class="container">
                <div class="text-center mb-5">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <span class="small font-weight-bold text-gradient-cyan">CORE CAPABILITIES</span>
                    </div>
                    <h2 class="font-weight-bolder text-white section-heading mb-3">
                        Engineered for Focus, Speed, and Predictability
                    </h2>
                    <p class="section-subheading mx-auto">
                        A high-velocity developer workspace engineered to eliminate administrative overhead, surface sprint bottlenecks, and preserve deep work.
                    </p>
                </div>

                <div class="row capabilities-grid">
                    <!-- Deep Work Preservation -->
                    <div class="col-md-6 col-lg-6 mb-4">
                        <div class="capability-card capability-emerald h-100">
                            <div class="capability-header">
                                <div class="capability-icon-wrap">
                                    <i class="bi bi-shield-check capability-icon text-emerald"></i>
                                </div>
                                <h3 class="capability-title text-white font-weight-bold mb-0">
                                    Deep Work Preservation
                                </h3>
                            </div>
                            <p class="capability-description mb-0">
                                Eliminate administrative timesheet drag. Log daily engineering hours in under 30 seconds without breaking flow state or fighting cumbersome portal forms.
                            </p>
                        </div>
                    </div>

                    <!-- Zero Context Switching -->
                    <div class="col-md-6 col-lg-6 mb-4">
                        <div class="capability-card capability-cyan h-100">
                            <div class="capability-header">
                                <div class="capability-icon-wrap">
                                    <i class="bi bi-window-sidebar capability-icon text-cyan"></i>
                                </div>
                                <h3 class="capability-title text-white font-weight-bold mb-0">
                                    Zero Context Switching
                                </h3>
                            </div>
                            <p class="capability-description mb-0">
                                Consolidate active sprint items, pending reviews, and personal branch statuses into one unified cockpit instead of maintaining dozens of open browser tabs.
                            </p>
                        </div>
                    </div>

                    <!-- Autonomous Sprint Pacing -->
                    <div class="col-md-6 col-lg-6 mb-4">
                        <div class="capability-card capability-amber h-100">
                            <div class="capability-header">
                                <div class="capability-icon-wrap">
                                    <i class="bi bi-speedometer2 capability-icon text-amber"></i>
                                </div>
                                <h3 class="capability-title text-white font-weight-bold mb-0">
                                    Autonomous Sprint Pacing
                                </h3>
                            </div>
                            <p class="capability-description mb-0">
                                Visual capacity indicators and daily pacing cues keep your sprint commitments balanced without standup friction or intrusive micromanagement.
                            </p>
                        </div>
                    </div>

                    <!-- Signal Over Noise -->
                    <div class="col-md-6 col-lg-6 mb-4">
                        <div class="capability-card capability-purple h-100">
                            <div class="capability-header">
                                <div class="capability-icon-wrap">
                                    <i class="bi bi-funnel-fill capability-icon text-purple"></i>
                                </div>
                                <h3 class="capability-title text-white font-weight-bold mb-0">
                                    Signal Over Noise
                                </h3>
                            </div>
                            <p class="capability-description mb-0">
                                Silence organization-wide notification clutter. The cockpit prioritizes only the work items, reviews, and discussion threads where your direct action is needed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Feature Showcase Tabs -->
        <section class="py-5 bg-surface" id="features">
            <div class="container">
                <div class="text-center mb-4 mb-md-5">
                    <span class="badge badge-pill badge-primary-subtle text-primary font-weight-bold mb-2">POWERFUL MODULES</span>
                    <h2 class="font-weight-bold text-dark">One Unified Engineering Dashboard</h2>
                    <p class="text-muted mx-auto" style="max-width: 620px;">
                        Stop juggling disconnected tools. Connect your daily time allocation, sprint backlog, and multi-repo code reviews into an integrated workflow.
                    </p>
                </div>

                <b-card no-body class="border-0 shadow-sm overflow-hidden feature-card">
                    <b-tabs pills card vertical nav-wrapper-class="col-md-4 p-3 border-right feature-tabs" content-class="col-md-8 p-4">
                        <!-- Tab 1: Timelog -->
                        <b-tab active>
                            <template #title>
                                <div class="d-flex align-items-center py-2">
                                    <i class="bi bi-clock-history feature-tab-icon text-primary mr-3"></i>
                                    <div>
                                        <div class="font-weight-bold text-left">Time & Daily Logging</div>
                                        <small class="text-muted text-left d-block">Rapid effort capture & category analytics</small>
                                    </div>
                                </div>
                            </template>
                            <div class="feature-detail">
                                <div class="d-flex align-items-center mb-2">
                                    <span class="badge badge-pill badge-primary-subtle text-primary font-weight-bold mr-2">TIMELOG ENGINE</span>
                                    <small class="text-muted">Live sync with DevOps boards</small>
                                </div>
                                <h4 class="font-weight-bold mb-2">Effort Tracking & Categorization</h4>
                                <p class="text-muted mb-3">
                                    Log work directly against assigned backlog items or general activities with single-click date increments. Effort breakdowns clearly categorize coding, architecture, reviews, and meetings.
                                </p>
                                <div class="feature-points-list">
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Two-way synchronization with connected DevOps time fields (Completed & Remaining Work)</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Dynamic monthly calendar with instant effort distribution breakdown and daily targets</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Continuous reconciliation between logged timesheet hours and connected board state</span>
                                    </div>
                                </div>
                                <div class="feature-action-bar mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between">
                                    <small class="text-muted mb-2 mb-sm-0">
                                        <i class="bi bi-check2-all text-primary mr-1"></i> Previewed in Hero Cockpit
                                    </small>
                                    <b-button size="sm" variant="outline-primary" class="font-weight-bold" @click="launchInstantDemo">
                                        Try in Live Sandbox &rarr;
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Tab 2: Backlog -->
                        <b-tab>
                            <template #title>
                                <div class="d-flex align-items-center py-2">
                                    <i class="bi bi-kanban feature-tab-icon text-info mr-3"></i>
                                    <div>
                                        <div class="font-weight-bold text-left">Backlog & Sprint Tasks</div>
                                        <small class="text-muted text-left d-block">Curated user-scoped task overview</small>
                                    </div>
                                </div>
                            </template>
                            <div class="feature-detail">
                                <div class="d-flex align-items-center mb-2">
                                    <span class="badge badge-pill badge-info-subtle text-info font-weight-bold mr-2">SPRINT BACKLOG</span>
                                    <small class="text-muted">Zero-WIQL query management</small>
                                </div>
                                <h4 class="font-weight-bold mb-2">Curated Personal Sprint Board</h4>
                                <p class="text-muted mb-3">
                                    Instant visibility into assigned user stories, bugs, and tasks across your entire organization without writing complex queries or digging through nested sprint trees.
                                </p>
                                <div class="feature-points-list">
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Instant work item state transitions (To Do, In Progress, Done) with zero reload latency</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Real-time remaining capacity calculations and over-allocated task indicator warnings</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>1-click Project Space switching across multiple Azure DevOps projects and teams</span>
                                    </div>
                                </div>
                                <div class="feature-action-bar mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between">
                                    <small class="text-muted mb-2 mb-sm-0">
                                        <i class="bi bi-check2-all text-info mr-1"></i> Previewed in Hero Cockpit
                                    </small>
                                    <b-button size="sm" variant="outline-info" class="font-weight-bold" @click="launchInstantDemo">
                                        Try in Live Sandbox &rarr;
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Tab 3: PR & Review Radar -->
                        <b-tab>
                            <template #title>
                                <div class="d-flex align-items-center py-2">
                                    <i class="bi bi-git feature-tab-icon text-warning mr-3"></i>
                                    <div>
                                        <div class="font-weight-bold text-left">PR & Review Radar</div>
                                        <small class="text-muted text-left d-block">Multi-repo pull request intelligence</small>
                                    </div>
                                </div>
                            </template>
                            <div class="feature-detail">
                                <div class="d-flex align-items-center mb-2">
                                    <span class="badge badge-pill badge-warning-subtle text-warning font-weight-bold mr-2">CODE REVIEWS</span>
                                    <small class="text-muted">Distributed repos & microservices</small>
                                </div>
                                <h4 class="font-weight-bold mb-2">Multi-Repo Pull Request Radar</h4>
                                <p class="text-muted mb-3">
                                    A single cockpit across distributed repositories and microservices. Keep code moving forward with unified visibility into every pull request where you are author or reviewer.
                                </p>
                                <div class="feature-points-list">
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Single cockpit for authored PRs, pending peer reviews, and completed merges</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Reviewer approvals, draft statuses, and discussion blocker flags at a glance</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Seamless deep-links into native DevOps pull request diff and discussion pages</span>
                                    </div>
                                </div>
                                <div class="feature-action-bar mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between">
                                    <small class="text-muted mb-2 mb-sm-0">
                                        <i class="bi bi-check2-all text-warning mr-1"></i> Previewed in Hero Cockpit
                                    </small>
                                    <b-button size="sm" variant="outline-warning" class="font-weight-bold" @click="launchInstantDemo">
                                        Try in Live Sandbox &rarr;
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Tab 4: Multi-Repo Branches -->
                        <b-tab>
                            <template #title>
                                <div class="d-flex align-items-center py-2">
                                    <i class="bi bi-bezier2 feature-tab-icon text-success mr-3"></i>
                                    <div>
                                        <div class="font-weight-bold text-left">Multi-Repo Branches</div>
                                        <small class="text-muted text-left d-block">Ahead/behind tracking & branch hygiene</small>
                                    </div>
                                </div>
                            </template>
                            <div class="feature-detail">
                                <div class="d-flex align-items-center mb-2">
                                    <span class="badge badge-pill badge-success-subtle text-success font-weight-bold mr-2">GIT HYGIENE</span>
                                    <small class="text-muted">Ahead/behind commit deltas</small>
                                </div>
                                <h4 class="font-weight-bold mb-2">Keep Repositories Clean & Synchronized</h4>
                                <p class="text-muted mb-3">
                                    Branch drift across distributed repositories causes merge conflicts and abandoned code. Track commit deltas, contributors, and sprint context across all repositories.
                                </p>
                                <div class="feature-points-list">
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Ahead and behind commit counters relative to target base branches</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Contributor and sprint filtering to quickly isolate your active feature branches</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Streamlined stale branch pruning to maintain clean remote repositories</span>
                                    </div>
                                </div>
                                <div class="feature-action-bar mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between">
                                    <small class="text-muted mb-2 mb-sm-0">
                                        <i class="bi bi-check2-all text-success mr-1"></i> Previewed in Hero Cockpit
                                    </small>
                                    <b-button size="sm" variant="outline-success" class="font-weight-bold" @click="launchInstantDemo">
                                        Try in Live Sandbox &rarr;
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>

                        <!-- Tab 5: Discussions & Mentions -->
                        <b-tab>
                            <template #title>
                                <div class="d-flex align-items-center py-2">
                                    <i class="bi bi-chat-left-text-fill feature-tab-icon text-danger mr-3"></i>
                                    <div>
                                        <div class="font-weight-bold text-left">Discussions & Mentions</div>
                                        <small class="text-muted text-left d-block">Work item threads & @mention signal radar</small>
                                    </div>
                                </div>
                            </template>
                            <div class="feature-detail">
                                <div class="d-flex align-items-center mb-2">
                                    <span class="badge badge-pill badge-danger-subtle text-danger font-weight-bold mr-2">SIGNAL RADAR</span>
                                    <small class="text-muted">Awaiting reply & mention flags</small>
                                </div>
                                <h4 class="font-weight-bold mb-2">Never Miss a Work Item Conversation</h4>
                                <p class="text-muted mb-3">
                                    DevOps discussions get lost in flooded email inboxes and buried across nested backlog cards. Spotlights threads where you are @mentioned or that require your decision.
                                </p>
                                <div class="feature-points-list">
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Smart <strong>Awaiting Answer</strong> & <strong>Awaiting Reply</strong> badges to prioritize conversations</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Full work item thread context with instant inline discussion replies and @mention tracking</span>
                                    </div>
                                    <div class="feature-point-row d-flex align-items-start mb-2">
                                        <i class="bi bi-check-circle-fill text-success mr-2 mt-1 flex-shrink-0"></i>
                                        <span>Unread indicators, lookback time windows, and instant keyboard search across discussions</span>
                                    </div>
                                </div>
                                <div class="feature-action-bar mt-3 pt-3 border-top d-flex flex-wrap align-items-center justify-content-between">
                                    <small class="text-muted mb-2 mb-sm-0">
                                        <i class="bi bi-check2-all text-danger mr-1"></i> Previewed in Hero Cockpit
                                    </small>
                                    <b-button size="sm" variant="outline-danger" class="font-weight-bold" @click="launchInstantDemo">
                                        Try in Live Sandbox &rarr;
                                    </b-button>
                                </div>
                            </div>
                        </b-tab>
                    </b-tabs>
                </b-card>
            </div>
        </section>

        <!-- Personal Time-Back Calculator Section -->
        <section class="py-5 landing-calculator-section" id="roi-calculator">
            <div class="container">
                <div class="text-center mb-5">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <i class="bi bi-calculator mr-2 text-info"></i>
                        <span class="small font-weight-bold text-gradient-cyan">YOUR TIME BACK</span>
                    </div>
                    <h2 class="font-weight-bold text-white mb-2">How Much Engineering Time Are You Losing?</h2>
                    <p class="section-subheading mx-auto text-light-muted" style="max-width: 640px;">
                        Developers spend an average of 15 to 25 minutes daily copying task identifiers, switching boards, and navigating clunky timesheets. See how much of your own time you get back:
                    </p>
                </div>

                <div class="row justify-content-center">
                    <div class="col-lg-10">
                        <div class="calculator-card p-4 p-md-5 rounded-xl shadow-2xl">
                            <div class="row align-items-center">
                                <!-- Sliders Column -->
                                <div class="col-md-6 pr-md-4 mb-4 mb-md-0 border-right-dark">
                                    <!-- Slider 1: Minutes Lost per Day -->
                                    <div class="mb-4">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <label for="minutes-lost-slider" class="small font-weight-bold text-white mb-0">
                                                Minutes You Lose Daily to Timesheets &amp; Board-Hopping
                                            </label>
                                            <span class="badge badge-pill badge-primary-subtle text-primary font-weight-bold px-2 py-1">
                                                {{ minutesLostPerDay }} min
                                            </span>
                                        </div>
                                        <input
                                            id="minutes-lost-slider"
                                            v-model.number="minutesLostPerDay" data-test="input-roi-minutes"
                                            type="range"
                                            min="5"
                                            max="45"
                                            step="5"
                                            class="custom-range aceternity-slider"
                                        />
                                        <div class="d-flex justify-content-between small text-light-muted mt-1">
                                            <span>5 min</span>
                                            <span>25 min</span>
                                            <span>45 min</span>
                                        </div>
                                    </div>

                                    <!-- Slider 2: Hourly Rate -->
                                    <div class="mb-3">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <label for="hourly-rate-slider" class="small font-weight-bold text-white mb-0">
                                                Your Hourly Rate
                                            </label>
                                            <span class="badge badge-pill badge-info-subtle text-info font-weight-bold px-2 py-1">
                                                ${{ hourlyRate }}/hr
                                            </span>
                                        </div>
                                        <input
                                            id="hourly-rate-slider"
                                            v-model.number="hourlyRate" data-test="input-roi-rate"
                                            type="range"
                                            min="25"
                                            max="150"
                                            step="5"
                                            class="custom-range aceternity-slider"
                                        />
                                        <div class="d-flex justify-content-between small text-light-muted mt-1">
                                            <span>$25</span>
                                            <span>$90</span>
                                            <span>$150</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- ROI Results Column -->
                                <div class="col-md-6 pl-md-4 roi-results-column">
                                    <div class="roi-results-grid">
                                        <div class="roi-metric-box p-3 rounded mb-3">
                                            <div class="small text-light-muted mb-1">
                                                <i class="bi bi-clock-history mr-1 text-info"></i> Your Hours Back per Sprint
                                            </div>
                                            <div class="h2 font-weight-bolder text-emerald mb-0">
                                                {{ hoursSavedPerSprint }} hrs
                                            </div>
                                            <small class="text-light-muted">Based on a 2-week sprint (10 working days)</small>
                                        </div>

                                        <div class="roi-submetrics-grid mb-3">
                                            <div class="roi-submetric-box p-3 rounded text-center">
                                                <div class="small text-light-muted roi-submetric-label">Per Year</div>
                                                <div class="h4 font-weight-bold text-gradient-cyan mb-0 mt-1">
                                                    +{{ annualHoursSaved }}h
                                                </div>
                                                <small class="text-light-muted roi-submetric-subtext">Back in Flow</small>
                                            </div>
                                            <div class="roi-submetric-box p-3 rounded text-center">
                                                <div class="small text-light-muted roi-submetric-label">Your Time's Worth</div>
                                                <div class="h4 font-weight-bold text-warning mb-0 mt-1">
                                                    ~${{ annualValueSaved }}
                                                </div>
                                                <small class="text-light-muted roi-submetric-subtext">@ ${{ hourlyRate }}/hr</small>
                                            </div>
                                        </div>

                                        <b-button
                                            block
                                            size="lg"
                                            class="btn-aceternity-primary font-weight-bold roi-cta-btn"
                                            @click="scrollToWaitlist"
                                        >
                                            <i class="bi bi-rocket-takeoff-fill mr-1"></i> Get My Time Back &bull; Join Beta
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Agile Comparison Grid -->
        <section class="py-5 border-top border-bottom bg-light" id="compare" data-test="section-comparison">
            <div class="container">
                <div class="text-center mb-5">
                    <span class="badge badge-pill badge-info text-white font-weight-bold mb-2">WHY VELOCYTRACK</span>
                    <h2 class="font-weight-bold text-dark">Built Specifically for Engineering Workflows</h2>
                    <p class="text-muted mx-auto" style="max-width: 600px;">
                        Standard time trackers are built for freelancers and lawyers. Backlog tools are built for project managers. VelocyTrack is built for developers and team leaders.
                    </p>
                </div>

                <div class="row justify-content-center">
                    <div class="col-xl-10">
                        <div class="d-md-none text-center text-muted small mb-2 comparison-swipe-hint">
                            <i class="bi bi-arrows-expand mr-1"></i> Swipe table horizontally to compare
                        </div>
                        <div class="table-responsive comparison-table-responsive">
                            <table class="table table-sm table-bordered shadow-sm comparison-table text-center align-middle">
                                <thead>
                                    <tr>
                                        <th class="text-left" style="width: 28%;">Capability</th>
                                        <th class="table-highlight text-info font-weight-bold" style="width: 24%;">
                                            <div class="d-inline-flex align-items-center justify-content-center">
                                                <i class="fa-solid fa-bolt mr-1"></i> VelocyTrack
                                            </div>
                                        </th>
                                        <th class="text-muted" style="width: 24%;">Generic Trackers<br /><small class="font-weight-normal">(Toggl, Harvest)</small></th>
                                        <th class="text-muted" style="width: 24%;">Backlog Tools<br /><small class="font-weight-normal">(Azure DevOps, Jira)</small></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td class="text-left font-weight-bold">Backlog Sync</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">Real-Time</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-x-circle text-danger comparison-icon mr-2"></i>
                                                <span class="text-muted">Manual Copy</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-info comparison-icon mr-2"></i>
                                                <span class="text-muted">Native Only</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-left font-weight-bold">Cross-Repo Pull Request Radar</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">Aggregated</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-x-circle text-danger comparison-icon mr-2"></i>
                                                <span class="text-muted">None</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-dash-circle text-warning comparison-icon mr-2"></i>
                                                <span class="text-muted">Repo by Repo</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-left font-weight-bold">Automated Capacity with PTO & Holidays</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">Built-In</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-x-circle text-danger comparison-icon mr-2"></i>
                                                <span class="text-muted">None</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-dash-circle text-warning comparison-icon mr-2"></i>
                                                <span class="text-muted">Requires Addons</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-left font-weight-bold">Developer-First Fast Logging UI</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">&lt; 3 Seconds</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle text-secondary comparison-icon mr-2"></i>
                                                <span class="text-muted">Timer Based</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-x-circle text-danger comparison-icon mr-2"></i>
                                                <span class="text-muted">Slow Modals</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-left font-weight-bold">Encrypted Credentials & Tenant Isolation</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle-fill text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">AES-Encrypted</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle text-secondary comparison-icon mr-2"></i>
                                                <span class="text-muted">Cloud Hosted</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle text-secondary comparison-icon mr-2"></i>
                                                <span class="text-muted">Enterprise</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-left font-weight-bold">Source Code Privacy</td>
                                        <td class="table-highlight">
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-shield-check text-success comparison-icon mr-2"></i>
                                                <span class="text-success font-weight-bold">Zero Code Stored</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-dash-circle text-muted comparison-icon mr-2"></i>
                                                <span class="text-muted">N/A (No Git)</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="comparison-cell-value d-inline-flex align-items-center justify-content-center">
                                                <i class="bi bi-check-circle text-secondary comparison-icon mr-2"></i>
                                                <span class="text-muted">Repository Host</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Mid-Page Conversion Hook -->
        <section class="py-5 landing-midpage-cta-section">
            <div class="container">
                <div class="midpage-cta-card p-4 p-md-5 rounded-xl shadow-2xl text-center position-relative overflow-hidden">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <span class="small font-weight-bold text-gradient-cyan">ZERO ADMINISTRATIVE FRICTION</span>
                    </div>
                    <h2 class="font-weight-bolder text-white mb-3">
                        Why waste 15 minutes a day battling bloated trackers?
                    </h2>
                    <p class="lead text-light-muted mx-auto mb-4" style="max-width: 680px;">
                        Native Azure DevOps time logging requires multiple clicks and modal transitions per work item. Standalone trackers lack commit or branch context. 
                        VelocyTrack bridges your code, reviews, and timesheets in <strong>under 3 seconds</strong>.
                    </p>
                    <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3">
                        <b-button
                            size="lg"
                            class="btn-aceternity-primary px-4 py-2 font-weight-bold mr-sm-3 mb-2 mb-sm-0 shadow-lg"
                            @click="scrollToWaitlist"
                        >
                            <i class="bi bi-person-check-fill mr-2"></i> Join Founding Cohort
                        </b-button>
                        <b-button
                            size="lg"
                            class="btn-aceternity-secondary px-4 py-2 font-weight-bold shadow-sm"
                            :disabled="isLaunchingInstantDemo || isLaunchingDemo"
                            @click="launchInstantDemo"
                        >
                            <b-spinner v-if="isLaunchingInstantDemo" small class="mr-2"></b-spinner>
                            <i v-else class="bi bi-lightning-charge-fill mr-2 text-warning"></i> 60-Second Instant Sandbox
                        </b-button>
                    </div>
                </div>
            </div>
        </section>

        <!-- Beta Testimonials & Community Reviews Section -->
        <section class="py-5 landing-testimonials-section" id="testimonials" data-test="section-testimonials">
            <div class="container">
                <div class="text-center mb-5">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <i class="bi bi-chat-quote-fill mr-2 text-info"></i>
                        <span class="small font-weight-bold text-gradient-cyan">COMMUNITY &amp; REVIEWS</span>
                    </div>
                    <h2 class="font-weight-bold text-white mb-2">What Beta Engineers Are Saying</h2>
                    <p class="section-subheading mx-auto text-light-muted mb-4" style="max-width: 680px;">
                        Real impressions from software developers and tech leads piloting VelocyTrack across their daily standups, pull requests, and multi-repo workflows.
                    </p>

                    <!-- DevHunt Tool of the Week Contest Ribbon -->
                    <div
                        v-if="isDevHuntLive"
                        class="d-flex justify-content-center mb-4"
                        data-test="devhunt-contest-ribbon"
                        data-state="active"
                    >
                        <div class="devhunt-contest-banner d-inline-flex align-items-center flex-wrap justify-content-center px-3 py-1 shadow-sm">
                            <span class="mr-2" style="font-size: 1.05rem;" aria-hidden="true">🔥</span>
                            <span class="contest-text font-weight-500 mr-2 text-light-muted">
                                We are live on <strong class="text-white">DevHunt</strong>: tool of the week contest
                            </span>
                            <a
                                href="https://devhunt.org/tool/velocytrack"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="devhunt-vote-btn d-inline-flex align-items-center"
                                title="Vote for VelocyTrack on DevHunt"
                                aria-label="Vote for VelocyTrack on DevHunt tool of the week contest"
                                data-test="btn-devhunt-contest-vote"
                            >
                                <span>check it out</span>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="ml-1" style="width: 13px; height: 13px;" aria-hidden="true">
                                    <path fill-rule="evenodd" d="M5 10a.75.75 0 01.75-.75h6.638L10.23 7.29a.75.75 0 111.04-1.08l3.5 3.25a.75.75 0 010 1.08l-3.5 3.25a.75.75 0 11-1.04-1.08l2.158-1.96H5.75A.75.75 0 015 10z" clip-rule="evenodd" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    <!-- Platform Badges & Community Hub (Product Hunt, DevHunt, BetaList & Peerlist) -->
                    <div class="platform-badges-wrapper d-flex flex-wrap justify-content-center align-items-center my-4" data-test="platform-badges-group">
                        <div class="m-2" data-test="ph-review-badge">
                            <a
                                href="https://www.producthunt.com/products/velocytrack/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-velocytrack"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="platform-badge-link shadow-sm"
                                title="Review VelocyTrack on Product Hunt"
                            >
                                <img
                                    src="https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=1340141&theme=dark"
                                    alt="VelocyTrack - DevOps cockpit &amp; smart time tracking for engineers | Product Hunt"
                                    style="width: 250px; height: 54px;"
                                    width="250"
                                    height="54"
                                />
                            </a>
                        </div>
                        <div class="m-2" data-test="devhunt-badge-wrap">
                            <a
                                href="https://devhunt.org/tool/velocytrack"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="platform-badge-link shadow-sm"
                                title="VelocyTrack on DevHunt"
                                data-test="link-devhunt-badge"
                            >
                                <img
                                    src="https://devhunt.org/badge/velocytrack.svg"
                                    alt="VelocyTrack - Featured on DevHunt"
                                    style="width: 220px; height: 54px;"
                                    width="220"
                                    height="54"
                                />
                            </a>
                        </div>
                        <div class="m-2" data-test="peerlist-badge-wrap">
                            <a
                                href="https://peerlist.io/jemyto/project/velocytrack"
                                target="_blank"
                                rel="noreferrer"
                                class="platform-badge-link shadow-sm"
                                title="VelocyTrack on Peerlist"
                                data-test="link-peerlist-badge"
                            >
                                <img
                                    src="https://peerlist.io/api/v1/projects/embed/PRJHQ7M68PD99MMNKIMRJRGJ8KRKO6?showUpvote=true&theme=dark"
                                    alt="VelocyTrack"
                                    style="width: auto; height: 72px;"
                                />
                            </a>
                        </div>
                        <div class="m-2" data-test="betalist-badge-wrap">
                            <a
                                href="https://betalist.com/startups/velocytrack?utm_campaign=badge-velocytrack&utm_medium=badge&utm_source=badge-featured"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="platform-badge-link shadow-sm"
                                title="VelocyTrack on BetaList"
                                data-test="link-betalist-badge"
                            >
                                <img
                                    src="https://betalist.com/badges/featured?id=192983&theme=dark"
                                    alt="VelocyTrack - Unify DevOps workflows and time tracking in a developer-first cockpit | BetaList"
                                    style="width: 156px; height: 54px;"
                                    width="156"
                                    height="54"
                                />
                            </a>
                        </div>
                    </div>
                </div>

                <!-- If 3+ verified database reviews exist, show the dynamic reviews grid -->
                <div class="row justify-content-center" v-if="testimonials && testimonials.length >= 3">
                    <div class="col-lg-4 col-md-6 mb-4" v-for="(t, idx) in testimonials" :key="t.id || idx">
                        <div class="testimonial-card h-100 p-4 d-flex flex-column">
                            <div class="d-flex align-items-center justify-content-between mb-3">
                                <div class="rating-stars text-warning">
                                    <i class="fas fa-star" v-for="s in (t.rating || 5)" :key="s"></i>
                                </div>
                                <span class="badge badge-pill" :class="getTestimonialBadgeClass(idx)">
                                    {{ t.metricBadge || t.category || 'Verified Review' }}
                                </span>
                            </div>
                            <blockquote class="testimonial-quote flex-grow-1">
                                &ldquo;{{ t.quote }}&rdquo;
                            </blockquote>
                            <div class="testimonial-author d-flex align-items-center pt-3 border-top-dark">
                                <div class="author-avatar mr-3" :class="getAuthorAvatarClass(idx)">
                                    {{ t.avatarInitials || 'BE' }}
                                </div>
                                <div>
                                    <div class="author-name font-weight-bold text-white">{{ t.authorName }}</div>
                                    <div class="author-role text-light-muted small">{{ t.role }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


            </div>
        </section>

        <!-- Enterprise Security & Data Sovereignty Section -->
        <section class="py-5 landing-security-section" id="security" data-test="section-security">
            <div class="container">
                <div class="text-center mb-5">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <i class="bi bi-shield-lock-fill mr-2 text-info"></i>
                        <span class="small font-weight-bold text-gradient-cyan">DATA PRIVACY &amp; SOVEREIGNTY</span>
                    </div>
                    <h2 class="font-weight-bold text-white mb-2">Built With Zero Trust &amp; Code Privacy by Design</h2>
                    <p class="section-subheading mx-auto text-light-muted" style="max-width: 720px;">
                        You connect VelocyTrack to your employer's Azure DevOps, so we treat security, code privacy, and credential isolation as foundational requirements.
                    </p>
                </div>

                <div class="row">
                    <!-- Security Card 1 -->
                    <div class="col-lg-3 col-md-6 mb-4">
                        <div class="security-card h-100 p-4 rounded-xl d-flex flex-column" data-test="card-security-code-privacy">
                            <div class="security-card-header">
                                <div class="security-card-icon">
                                    <i class="bi bi-code-slash text-info"></i>
                                </div>
                                <h5 class="font-weight-bold text-white security-card-title mb-0">No Source Code Storage</h5>
                            </div>
                            <p class="text-light-muted small mb-0 flex-grow-1">
                                We never clone, mirror, or permanently store your source code files. All diff previews and code discussions are streamed transiently in your browser session via official APIs.
                            </p>
                        </div>
                    </div>

                    <!-- Security Card 2 -->
                    <div class="col-lg-3 col-md-6 mb-4">
                        <div class="security-card h-100 p-4 rounded-xl d-flex flex-column" data-test="card-security-encryption">
                            <div class="security-card-header">
                                <div class="security-card-icon">
                                    <i class="bi bi-key-fill text-warning"></i>
                                </div>
                                <h5 class="font-weight-bold text-white security-card-title mb-0">AES-256 at Rest</h5>
                            </div>
                            <p class="text-light-muted small mb-0 flex-grow-1">
                                Integration credentials and Personal Access Tokens are encrypted at rest using industry-standard AES-256. All browser-to-server traffic is TLS 1.3 enforced.
                            </p>
                        </div>
                    </div>

                    <!-- Security Card 3 -->
                    <div class="col-lg-3 col-md-6 mb-4">
                        <div class="security-card h-100 p-4 rounded-xl d-flex flex-column" data-test="card-security-eu-hosting">
                            <div class="security-card-header">
                                <div class="security-card-icon">
                                    <i class="bi bi-globe-europe-africa text-success"></i>
                                </div>
                                <h5 class="font-weight-bold text-white security-card-title mb-0">EU Data Hosting</h5>
                            </div>
                            <p class="text-light-muted small mb-0 flex-grow-1">
                                Hosted on dedicated European Union infrastructure with strict GDPR compliance and tenant isolation. No third-party data broker access or AI model training on your data.
                            </p>
                        </div>
                    </div>

                    <!-- Security Card 4 -->
                    <div class="col-lg-3 col-md-6 mb-4">
                        <div class="security-card h-100 p-4 rounded-xl d-flex flex-column" data-test="card-security-scoped-pats">
                            <div class="security-card-header">
                                <div class="security-card-icon">
                                    <i class="bi bi-shield-check text-info"></i>
                                </div>
                                <h5 class="font-weight-bold text-white security-card-title mb-0">Least-Privilege Scopes</h5>
                            </div>
                            <p class="text-light-muted small mb-0 flex-grow-1">
                                Only minimal read/write scopes required for your work items and active branches. Disconnect or revoke token access anytime directly from Azure DevOps.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Trust Reassurance Banner -->
                <div class="security-trust-bar p-3 rounded-pill mx-auto mt-2 d-flex flex-wrap align-items-center justify-content-center text-center">
                    <span class="small text-light-muted mr-3 mb-1 mb-md-0">
                        <i class="bi bi-credit-card-2-front text-info mr-1"></i> Payments &amp; Invoicing via <strong>Creem (Merchant of Record)</strong>
                    </span>
                    <span class="text-muted d-none d-md-inline mr-3">&bull;</span>
                    <span class="small text-light-muted mr-3 mb-1 mb-md-0">
                        <i class="bi bi-patch-check text-success mr-1"></i> Responsible Disclosure: <code>security@velocytrack.com</code>
                    </span>
                    <span class="text-muted d-none d-md-inline mr-3">&bull;</span>
                    <span class="small">
                        <a href="/acceptable-use" @click.prevent="openLegal('security')" class="text-gradient-cyan text-decoration-none font-weight-bold" data-test="link-security-policy">
                            Read Security &amp; Compliance Policy &rarr;
                        </a>
                    </span>
                </div>
            </div>
        </section>

        <!-- Pricing Section -->
        <section class="py-5 landing-pricing-section" id="pricing" data-test="section-pricing">
            <div class="container">
                <div class="text-center mb-5">
                    <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                        <i class="fa-solid fa-crown mr-2 text-warning"></i>
                        <span class="small font-weight-bold text-gradient-cyan">TRANSPARENT PRICING</span>
                    </div>
                    <h2 class="font-weight-bold text-white mb-2">Simple, Predictable Plans for Developers &amp; Teams</h2>
                    <p class="section-subheading mx-auto text-light-muted" style="max-width: 680px;">
                        Start free forever with individual time tracking, or unlock full multi-repo project spaces with Pro. No hidden setup fees or enterprise lock-in.
                    </p>
                </div>

                <!-- Early-Bird Launch Promotion Ribbon -->
                <div class="pricing-guarantee-ribbon p-3 rounded-xl mb-5 mx-auto text-center position-relative" data-test="pricing-beta-guarantee">
                    <div class="d-inline-flex flex-wrap align-items-center justify-content-center mb-1">
                        <span class="badge badge-pill badge-warning font-weight-bold px-2 py-1 mr-2">EARLY-BIRD LAUNCH PROMOTION</span>
                        <strong class="text-white">100% Free Pro in Beta &bull; 50% Off First Year (Beta + 1st Month of Launch)</strong>
                    </div>
                    <p class="small text-light-muted mb-0 mx-auto" style="max-width: 740px;">
                        Join the open beta now for instant 100% free Pro access (limited cohort seats). When commercial billing begins, the early-bird offer extends to <strong>all beta members and everyone who signs up during the first month of official release</strong>: 50% off your entire first year.
                    </p>
                </div>

                <!-- 3 Pricing Cards Grid -->
                <div class="row justify-content-center mb-4">
                    <div
                        v-for="tier in pricingTiers"
                        :key="tier.id"
                        class="col-lg-4 col-md-6 mb-4"
                    >
                        <div
                            class="pricing-tier-card h-100 p-4 rounded-xl d-flex flex-column"
                            :class="{ 'pricing-tier-popular': tier.highlight }"
                            :data-test="`pricing-card-${tier.id}`"
                        >
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h4 class="font-weight-bold text-white mb-0">{{ tier.name }}</h4>
                                <span v-if="tier.badge" class="badge" :class="tier.highlight ? 'badge-primary' : 'badge-secondary'">
                                    {{ tier.badge }}
                                </span>
                            </div>

                            <p class="text-light-muted small mb-3 pricing-card-desc">{{ tier.desc }}</p>

                            <div class="pricing-price-wrap mb-2">
                                <span class="pricing-price-main font-weight-bolder text-white">{{ tier.priceDisplay }}</span>
                                <span v-if="tier.period" class="pricing-price-period text-light-muted small">{{ tier.period }}</span>
                            </div>

                            <div class="pricing-subtext text-success small font-weight-bold mb-4">
                                <i class="bi bi-check-circle-fill mr-1"></i>{{ tier.subtext }}
                            </div>

                            <ul class="pricing-feature-list list-unstyled mb-0 flex-grow-1">
                                <li v-for="(feat, fIdx) in tier.features" :key="fIdx" class="d-flex align-items-start mb-2 small text-light-muted">
                                    <i class="bi bi-check2 text-info mr-2 font-weight-bold flex-shrink-0 mt-1"></i>
                                    <span>{{ feat }}</span>
                                </li>
                            </ul>

                            <div v-if="false" class="pricing-cta-wrap mt-auto">
                                <b-button
                                    v-if="tier.ctaAction === 'demo'"
                                    block
                                    size="lg"
                                    variant="outline-light"
                                    class="font-weight-bold btn-pricing-card"
                                    :disabled="isLaunchingInstantDemo || isLaunchingDemo"
                                    @click="launchInstantDemo"
                                    :data-test="`btn-pricing-${tier.id}`"
                                >
                                    <i class="bi bi-play-circle-fill mr-1 text-info"></i> {{ tier.ctaText }}
                                </b-button>
                                <b-button
                                    v-else-if="tier.ctaAction === 'waitlist'"
                                    block
                                    size="lg"
                                    class="btn-aceternity-primary font-weight-bold btn-pricing-card shadow-lg"
                                    @click="scrollToWaitlist"
                                    :data-test="`btn-pricing-${tier.id}`"
                                >
                                    <i class="bi bi-rocket-takeoff-fill mr-1"></i> {{ tier.ctaText }}
                                </b-button>
                                <b-button
                                    v-else
                                    block
                                    size="lg"
                                    variant="outline-info"
                                    class="font-weight-bold btn-pricing-card"
                                    @click="scrollToLaunch"
                                    :data-test="`btn-pricing-${tier.id}`"
                                >
                                    <i class="bi bi-bell-fill mr-1 text-warning"></i> {{ tier.ctaText }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Deep Comparison Matrix Link -->
                <div class="text-center mt-3">
                    <router-link v-if="false" to="/plans" class="btn btn-outline-info px-4 py-2 font-weight-bold rounded-pill shadow-sm" data-test="link-view-all-plans">
                        <i class="bi bi-grid-3x3-gap-fill mr-2"></i> Compare All Plan Features &rarr;
                    </router-link>
                </div>
            </div>
        </section>

        <!-- Waitlist Registration (Launch Gate) -->
        <section class="py-5 landing-waitlist-section" id="waitlist">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <b-card class="shadow-lg border-0 aceternity-card">
                            <div class="text-center mb-4">
                                <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1">
                                    <span class="small font-weight-bold text-gradient-cyan">OPEN BETA &bull; FOUNDING COHORT &bull; LIMITED INSTANT SEATS</span>
                                </div>
                                <h3 class="font-weight-bold waitlist-title">Claim Your Beta Spot</h3>
                                <p class="waitlist-subtitle mb-3">
                                    VelocyTrack is in open beta. Claim your spot to get an instant invitation link (limited cohort seats; once instant capacity is reached, new claims are onboarded in batches), direct roadmap influence, free beta access, and early-bird pricing: <strong>50% off your first year</strong> for all beta members and anyone who signs up during the <strong>first month after official release</strong>.
                                </p>

                                <!-- Social Proof Strip in Card -->
                                <div class="d-flex justify-content-center mb-2">
                                    <social-proof-strip :social-proof="waitlistStatus.socialProof"></social-proof-strip>
                                </div>
                            </div>

                            <!-- Closed Beta Notice if Waitlist:BetaClaimsOpen is false -->
                            <div v-if="!waitlistStatus.isOpen" class="text-center py-4" data-test="container-beta-closed">
                                <b-alert show variant="warning" class="mb-4">
                                    <i class="bi bi-lock-fill mr-2"></i>
                                    <strong>The beta cohort has reached capacity.</strong><br />
                                    New automated invitations are paused. Subscribe below to be first in line when we officially launch!
                                </b-alert>
                                <b-button
                                    size="lg"
                                    class="btn-aceternity-primary font-weight-bold px-4"
                                    @click="scrollToLaunch"
                                >
                                    <i class="bi bi-bell-fill mr-2 text-warning"></i> Notify Me at Official Launch
                                </b-button>
                            </div>

                            <!-- Success / Status Feedback Banners -->
                            <template v-if="registrationSuccess">
                                <b-alert
                                    v-if="claimResult.status === 'invited'"
                                    show
                                    data-test="alert-waitlist-invited"
                                    variant="success"
                                    dismissible
                                    class="mb-4 text-center aceternity-success-alert"
                                    @dismissed="registrationSuccess = false"
                                >
                                    <i class="bi bi-patch-check-fill mr-2" style="font-size: 1.35rem;"></i>
                                    <h5 class="alert-heading font-weight-bold d-inline">You're In! Start in One Click</h5>
                                    <p class="mb-0 mt-2">
                                        Your beta spot is reserved for <strong>{{ form.email }}</strong>. Skip the inbox: continue with the Google or GitHub account that uses this email.
                                    </p>
                                    <div class="waitlist-quickstart mt-3 mx-auto" data-test="waitlist-quickstart">
                                        <google-sign-in :clientId="googleClientId" :width="320" theme="filled_black" text="continue_with" :suppressToast="true" :invitationToken="claimResult.token" @error="onQuickStartError" @success="onQuickStartSuccess" @beta-claim-pending="onQuickStartClaimPending" class="w-100 mb-2 mx-auto" data-test="btn-waitlist-google"></google-sign-in>
                                        <b-button variant="outline-light" class="w-100 d-flex align-items-center sso-btn px-3" data-test="btn-waitlist-github" aria-label="Continue with GitHub" title="Continue with GitHub" @click="loginOAuth('GitHub')">
                                            <i class="bi bi-github mr-2"></i>
                                            <span>Continue with GitHub</span>
                                        </b-button>
                                    </div>
                                    <p class="small mt-3 mb-0">Prefer email? Open the invitation link we just sent you, or sign in later.</p>
                                    <div class="mt-2">
                                        <router-link to="/sign-in" class="btn btn-sm btn-outline-light font-weight-bold">
                                            Go to Sign In &rarr;
                                        </router-link>
                                    </div>
                                </b-alert>

                                <b-alert
                                    v-else-if="claimResult.status === 'queued'"
                                    show
                                    data-test="alert-waitlist-queued"
                                    variant="info"
                                    dismissible
                                    class="mb-4 text-center aceternity-info-alert"
                                    @dismissed="registrationSuccess = false"
                                >
                                    <i class="bi bi-hourglass-split mr-2" style="font-size: 1.35rem;"></i>
                                    <h5 class="alert-heading font-weight-bold d-inline">Spot Claimed &bull; You're in Queue!</h5>
                                    <p class="mb-0 mt-2">
                                        Thank you! We've reserved your spot for <strong>{{ form.email }}</strong>. Due to high demand, we onboard engineers in batches to ensure dedicated support and system stability. We will send your invitation link as soon as your batch unlocks.
                                    </p>
                                </b-alert>

                                <b-alert
                                    v-else-if="claimResult.status === 'alreadyInvited'"
                                    show
                                    data-test="alert-waitlist-already-invited"
                                    variant="primary"
                                    dismissible
                                    class="mb-4 text-center"
                                    @dismissed="registrationSuccess = false"
                                >
                                    <i class="bi bi-info-circle-fill mr-2" style="font-size: 1.35rem;"></i>
                                    <h5 class="alert-heading font-weight-bold d-inline">Already Invited!</h5>
                                    <p class="mb-0 mt-2">
                                        An active invitation already exists for <strong>{{ form.email }}</strong>. We've refreshed your invite link. You can also sign in right now in one click:
                                    </p>
                                    <div class="waitlist-quickstart mt-3 mx-auto" data-test="waitlist-quickstart-reinvite">
                                        <google-sign-in :clientId="googleClientId" :width="320" theme="filled_black" text="continue_with" :suppressToast="true" :invitationToken="claimResult.token" @error="onQuickStartError" @success="onQuickStartSuccess" @beta-claim-pending="onQuickStartClaimPending" class="w-100 mb-2 mx-auto" data-test="btn-reinvite-google"></google-sign-in>
                                        <b-button variant="outline-light" class="w-100 d-flex align-items-center sso-btn px-3" data-test="btn-reinvite-github" aria-label="Continue with GitHub" title="Continue with GitHub" @click="loginOAuth('GitHub')">
                                            <i class="bi bi-github mr-2"></i>
                                            <span>Continue with GitHub</span>
                                        </b-button>
                                    </div>
                                    <div class="mt-3">
                                        <router-link to="/sign-in" class="btn btn-sm btn-outline-light font-weight-bold">
                                            Sign In to Beta &rarr;
                                        </router-link>
                                    </div>
                                </b-alert>

                                <b-alert
                                    v-else-if="claimResult.status === 'alreadyClaimed'"
                                    show
                                    data-test="alert-waitlist-already-claimed"
                                    variant="info"
                                    dismissible
                                    class="mb-4 text-center"
                                    @dismissed="registrationSuccess = false"
                                >
                                    <i class="bi bi-check-circle-fill mr-2" style="font-size: 1.35rem;"></i>
                                    <h5 class="alert-heading font-weight-bold d-inline">Spot Already Reserved</h5>
                                    <p class="mb-0 mt-2">
                                        A beta spot reservation for <strong>{{ form.email }}</strong> is already registered. We'll dispatch your invitation email as soon as seats release.
                                    </p>
                                </b-alert>

                                <b-alert
                                    v-else
                                    show
                                    data-test="alert-waitlist-success"
                                    variant="success"
                                    dismissible
                                    class="mb-4 text-center aceternity-success-alert"
                                    @dismissed="registrationSuccess = false"
                                >
                                    <i class="bi bi-check-circle-fill mr-2" style="font-size: 1.25rem;"></i>
                                    <h5 class="alert-heading font-weight-bold d-inline">Spot Claimed!</h5>
                                    <p class="mb-0 mt-2">
                                        {{ claimResult.message || "We've received your spot reservation and sent a confirmation email." }}
                                    </p>
                                </b-alert>
                            </template>

                            <!-- Error Banner -->
                            <b-alert
                                :show="!!errorMessage"
                                data-test="alert-waitlist-error"
                                variant="danger"
                                dismissible
                                class="mb-4"
                                @dismissed="errorMessage = ''"
                            >
                                <i class="bi bi-exclamation-triangle-fill mr-2"></i>
                                {{ errorMessage }}
                            </b-alert>

                            <!-- Waitlist Form (4 Fields ONLY: email, name, role, platform multipick) -->
                            <ValidationObserver v-if="waitlistStatus.isOpen && !registrationSuccess" ref="waitlistObserver" v-slot="{ handleSubmit }">
                                <b-form @submit.prevent="handleSubmit(onRegister)" @focusin="initTurnstile">
                                    <!-- Anti-bot honeypot field (hidden from human visitors) -->
                                    <div style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" aria-hidden="true">
                                        <label for="honeypot-website">Company Website</label>
                                        <input
                                            id="honeypot-website"
                                            v-model="form.website"
                                            type="text"
                                            name="website"
                                            tabindex="-1"
                                            autocomplete="off"
                                        />
                                    </div>

                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <ValidationProvider name="Email" rules="required|email" v-slot="{ errors }">
                                                <b-form-group label="Email *" label-class="font-weight-bold small field-label">
                                                    <b-form-input
                                                        v-model="form.email"
                                                        data-test="input-waitlist-email"
                                                        type="email"
                                                        placeholder="you@example.com"
                                                        class="aceternity-input"
                                                        :state="errors.length > 0 ? false : null"
                                                        required
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <b-form-group label="Full Name" label-class="font-weight-bold small field-label">
                                                <b-form-input
                                                    v-model="form.fullName"
                                                    data-test="input-waitlist-fullname"
                                                    placeholder="Alex Morgan"
                                                    class="aceternity-input"
                                                ></b-form-input>
                                            </b-form-group>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-md-12 mb-3">
                                            <b-form-group label="Your Role" label-class="font-weight-bold small field-label">
                                                <b-form-select
                                                    v-model="form.role"
                                                    data-test="select-waitlist-role"
                                                    :options="roleOptions"
                                                    class="aceternity-select"
                                                ></b-form-select>
                                            </b-form-group>
                                        </div>
                                    </div>

                                    <div class="mb-4">
                                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 platform-integration-header">
                                            <label class="font-weight-bold small field-label mb-0 text-nowrap">
                                                Platform Integration
                                            </label>
                                            <span class="badge badge-pill badge-phase-1 py-1 px-2 font-weight-bold d-none d-sm-inline-flex align-items-center">
                                                <i class="bi bi-check-circle-fill mr-1"></i>Azure DevOps Live in Beta
                                            </span>
                                            <span class="badge badge-pill badge-phase-1 py-1 px-2 font-weight-bold d-inline-flex d-sm-none align-items-center">
                                                <i class="bi bi-check-circle-fill mr-1"></i>Live in Beta
                                            </span>
                                        </div>
                                        <div class="platform-multipick-grid">
                                            <div
                                                v-for="p in availablePlatforms"
                                                :key="p.id"
                                                :data-test="`platform-option-${p.id}`"
                                                class="platform-chip-badge-card"
                                                :class="{
                                                    'platform-chip-selected platform-chip-card-active': p.isSupported,
                                                    'platform-chip-card-roadmap': !p.isSupported
                                                }"
                                            >
                                                <div class="d-flex align-items-center justify-content-between w-100 mb-1">
                                                    <div class="d-flex align-items-center text-truncate mr-1">
                                                        <i :class="[p.icon, 'mr-2 flex-shrink-0']" :style="{ color: p.color }"></i>
                                                        <span class="platform-chip-name text-truncate">{{ p.name }}</span>
                                                    </div>
                                                    <i v-if="p.isSupported" class="bi bi-check2-circle ml-1 text-info platform-chip-check flex-shrink-0" title="Active in Beta"></i>
                                                    <i v-else class="bi bi-hourglass-split ml-1 text-muted platform-chip-upcoming flex-shrink-0" title="Coming Next"></i>
                                                </div>
                                                <div class="platform-chip-meta w-100 d-flex justify-content-between align-items-center">
                                                    <span class="badge badge-pill platform-chip-phase-badge" :class="p.isSupported ? 'badge-phase-1' : 'badge-phase-2'">
                                                        {{ p.phase }}
                                                    </span>
                                                    <span class="small platform-chip-status-text d-none d-md-inline" :class="p.isSupported ? 'text-info font-weight-bold' : 'text-muted'">
                                                        {{ p.status }}
                                                    </span>
                                                    <span v-if="p.isSupported" class="sr-only">Live in Beta</span>
                                                </div>
                                            </div>
                                        </div>
                                    <div class="small text-light-muted mt-2 d-flex align-items-start">
                                            <i class="bi bi-info-circle text-info mr-2 mt-1 flex-shrink-0"></i>
                                            <span>
                                                VelocyTrack Beta currently connects to <strong>Azure DevOps</strong>. Connecting GitHub, GitLab, and Jira Software is coming next.
                                            </span>
                                        </div>
                                    </div>

                                    <div ref="turnstileWaitlistContainer" class="my-3 d-flex justify-content-center"></div>

                                    <div class="text-center mt-4">
                                        <b-button
                                            type="submit"
                                            size="lg"
                                            data-test="btn-submit-waitlist"
                                            :data-state="isSubmitting ? 'submitting' : 'idle'"
                                            class="btn-aceternity-primary btn-block py-3 font-weight-bold shadow-lg"
                                            :disabled="isSubmitting"
                                        >
                                            <b-spinner v-if="isSubmitting" small class="mr-2"></b-spinner>
                                            <i v-else class="bi bi-check2-circle mr-2"></i>
                                            Claim My Beta Spot
                                        </b-button>
                                        <div class="small waitlist-terms mt-3">
                                            By submitting, you agree to our 
                                            <a href="#" @click.prevent="openLegal('terms')" class="text-gradient-cyan text-decoration-none">Terms of Service</a>
                                            and acknowledge our 
                                            <a href="#" @click.prevent="openLegal('privacy')" class="text-gradient-cyan text-decoration-none">Privacy Policy</a>. 
                                            No credit card required. Free beta cohort access.
                                        </div>
                                        <div class="waitlist-invite-note mt-3 pt-3">
                                            <span class="small text-light-muted">
                                                Have a beta invite?
                                                <router-link to="/sign-in" data-test="link-waitlist-beta-signin" class="text-gradient-cyan font-weight-bold ml-1 text-decoration-none hover-underline">
                                                    Sign in here &rarr;
                                                </router-link>
                                            </span>
                                        </div>
                                    </div>
                                </b-form>
                            </ValidationObserver>
                        </b-card>
                    </div>
                </div>
            </div>
        </section>

        <!-- Official Launch Updates Section -->
        <section class="py-5 landing-launch-section" id="launch-notify" data-test="section-launch-notify">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <div class="launch-notify-card p-4 p-md-5 rounded-xl shadow-2xl text-center position-relative overflow-hidden">
                            <!-- Glowing background radial aura -->
                            <div class="launch-card-aura" aria-hidden="true"></div>

                            <div class="d-inline-flex align-items-center aceternity-pill mb-3 px-3 py-1 position-relative">
                                <span class="pulse-dot mr-2"></span>
                                <span class="small font-weight-bold text-gradient-cyan">OFFICIAL LAUNCH NOTIFICATION</span>
                            </div>

                            <h2 class="font-weight-bolder text-white mb-3 launch-title">
                                Be the First to Know When We Officially Launch
                            </h2>
                            <p class="lead text-light-muted mx-auto mb-3 launch-subtitle">
                                The <strong>1st official release</strong> will launch with full native <strong>Azure DevOps (ADO)</strong> support &mdash; delivering unified sprint backlog sync, in-grid fast time logging, cross-repo PR radar, and team capacity analytics.
                            </p>
                            <div class="d-inline-flex flex-wrap align-items-center justify-content-center aceternity-chip-badge py-1 px-3 mb-4 mx-auto">
                                <span class="text-light-muted mr-2">Platform Roadmap:</span>
                                <span class="text-info font-weight-bold mr-2"><i class="icon-azure mr-1"></i>Azure DevOps (1st Release)</span>
                                <span class="text-light-muted mr-2">&rarr;</span>
                                <span class="text-light-muted"><i class="fa-brands fa-github mr-1"></i>GitHub &bull; <i class="fa-brands fa-gitlab text-warning mr-1"></i>GitLab &bull; <i class="fa-brands fa-jira text-primary mr-1"></i>Jira (Later in Roadmap)</span>
                            </div>

                            <!-- Success Banner -->
                            <b-alert
                                :show="launchSuccess"
                                data-test="alert-launch-success"
                                variant="success"
                                dismissible
                                class="mb-4 text-center aceternity-success-alert"
                                @dismissed="launchSuccess = false"
                            >
                                <i class="bi bi-check-circle-fill mr-2" style="font-size: 1.25rem;"></i>
                                <h5 class="alert-heading font-weight-bold d-inline">You're on the launch announcement list!</h5>
                                <p class="mb-0 mt-2">
                                    {{ launchSuccessMessage || "We'll notify you the moment VelocyTrack officially launches to the public." }}
                                </p>
                            </b-alert>

                            <!-- Error Banner -->
                            <b-alert
                                :show="!!launchError"
                                data-test="alert-launch-error"
                                variant="danger"
                                dismissible
                                class="mb-4"
                                @dismissed="launchError = ''"
                            >
                                <i class="bi bi-exclamation-triangle-fill mr-2"></i>
                                {{ launchError }}
                            </b-alert>

                            <!-- Single-Line High-Converting Launch Capture Form -->
                            <b-form v-if="!launchSuccess" @submit.prevent="onSubscribeLaunchAsync" class="launch-form mx-auto mb-4" data-test="form-launch-subscribe">
                                <!-- Anti-bot honeypot field -->
                                <div style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" aria-hidden="true">
                                    <label for="launch-bot-field">Company Domain</label>
                                    <input
                                        id="launch-bot-field"
                                        v-model="launchWebsite"
                                        type="text"
                                        name="company_domain"
                                        tabindex="-1"
                                        autocomplete="off"
                                    />
                                </div>

                                <div class="launch-input-group d-flex flex-column flex-sm-row align-items-stretch">
                                    <b-form-input
                                        v-model="launchEmail"
                                        data-test="input-launch-email"
                                        type="email"
                                        placeholder="Enter your email (e.g. you@example.com)"
                                        class="aceternity-input launch-email-input flex-grow-1 mr-sm-2 mb-2 mb-sm-0"
                                        required
                                    ></b-form-input>
                                    <b-button
                                        type="submit"
                                        size="lg"
                                        data-test="btn-submit-launch-notify"
                                        :data-state="isLaunchSubmitting ? 'submitting' : 'idle'"
                                        class="btn-aceternity-primary font-weight-bold px-4 launch-submit-btn flex-shrink-0"
                                        :disabled="isLaunchSubmitting || !launchEmail"
                                    >
                                        <b-spinner v-if="isLaunchSubmitting" small class="mr-2"></b-spinner>
                                        <i v-else class="bi bi-bell-fill mr-2 text-warning"></i>
                                        Notify Me at Launch
                                    </b-button>
                                </div>
                            </b-form>

                            <!-- Launch Perks / Badges -->
                            <div class="d-flex flex-wrap justify-content-center align-items-center launch-perks-row mt-3">
                                <div class="launch-perk-item d-flex align-items-center mx-2 my-1">
                                    <i class="bi bi-rocket-takeoff-fill text-info mr-2"></i>
                                    <span class="small text-light-muted">Day-One ADO Launch Alert</span>
                                </div>
                                <div class="launch-perk-item d-flex align-items-center mx-2 my-1">
                                    <i class="bi bi-tag-fill text-warning mr-2"></i>
                                    <span class="small text-light-muted">Early-Bird 50% Off Year 1 (Beta + Month 1)</span>
                                </div>
                                <div class="launch-perk-item d-flex align-items-center mx-2 my-1">
                                    <i class="bi bi-shield-check text-success mr-2"></i>
                                    <span class="small text-light-muted">Zero Spam Guarantee</span>
                                </div>
                            </div>

                            <div class="mt-4 pt-2 border-top-dark">
                                <small class="text-light-muted">
                                    Want hands-on access right now? 
                                    <a href="#waitlist" @click.prevent="scrollToWaitlist" class="text-gradient-cyan font-weight-bold text-decoration-none ml-1">
                                        Join Founding Cohort 1 &rarr;
                                    </a>
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="py-5 bg-light" id="faq">
            <div class="container">
                <div class="text-center mb-5">
                    <span class="badge badge-pill badge-secondary text-white font-weight-bold mb-2">QUESTIONS & ANSWERS</span>
                    <h2 class="font-weight-bold text-dark">Frequently Asked Questions</h2>
                </div>

                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <div class="accordion" role="tablist">
                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-1 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>What is VelocyTrack?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-1" visible accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        VelocyTrack is a developer-first engineering cockpit and time intelligence platform that combines sprint backlogs, active git branches, code reviews, and frictionless time tracking into a unified workspace. Instead of navigating sluggish platform tabs or generic spreadsheets, engineers log effort directly against work items in seconds, while teams gain automated sprint capacity metrics and velocity visibility without storing or cloning any source code.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-2 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Who is it for?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-2" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        VelocyTrack is designed for software developers, tech leads, engineering managers, and contractors working with agile DevOps boards:
                                        <ul class="mt-2 mb-0 pl-3">
                                            <li class="mb-1"><strong>Individual Developers &amp; Contractors:</strong> Engineers who want fast, frictionless daily time logging tied to their real tickets, PRs, and branches without administrative hassle.</li>
                                            <li class="mb-1"><strong>Tech Leads &amp; Scrum Masters:</strong> Leaders seeking real-time sprint pacing, team availability/PTO insights, and PR review bottleneck awareness without micromanaging daily standups.</li>
                                            <li><strong>Engineering Organizations:</strong> Teams running Azure DevOps (with GitHub and Jira upcoming) that require reliable project accounting, capacity forecasting, and strict enterprise security without granting access to proprietary source code.</li>
                                        </ul>
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-3 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>How does the open beta work?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-3" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Claim your spot with your work email and our automated onboarding sends an invitation link straight to your inbox &mdash; no manual approval. Beta members get full Pro access on the beta instance for free, for as long as they use it and help improve the product with feedback and issue reports. If instant seats for your platform are temporarily full, you're placed first in line and invited as soon as capacity frees up.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-4 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>How are my DevOps credentials and access tokens protected?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-4" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        All Personal Access Tokens (PATs) and credentials are encrypted at rest using AES-256 and transmitted exclusively over TLS 1.3. We only request scoped permissions necessary to read and update assigned work items, branches, and pull requests. We never inspect, store, or train on your source code repository contents. Furthermore, live repository queries remain transient in browser memory, and you can revoke token access from your provider settings at any time.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-5 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Can I explore VelocyTrack without connecting my organization's DevOps account?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-5" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Yes. Click the <strong>Instant Demo</strong> button in the navigation or hero section to launch an isolated sandbox environment. It comes pre-loaded with simulated sprint work items, multi-repo pull requests, active branch diffs, and capacity metrics &mdash; no sign-up, credit card, or credentials required.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-6 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>What are the pricing plans after the Beta period?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-6" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        VelocyTrack offers a permanent <strong>Free tier (€0/forever)</strong> for individual engineers with full sprint time tracking and backlog exploration. During the open beta, all <strong>Pro Plan</strong> features are unlocked <strong>100% free</strong>, with official subscription pricing to be announced at launch. Team plans with cross-team capacity rollups, velocity analytics, and multi-platform aggregation are coming next. All open beta members and anyone who signs up during the <strong>first month after official release</strong> lock in an automatic <strong>50% early-bird discount on year one</strong>.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-7 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Does VelocyTrack store or clone our proprietary source code?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-7" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        No. VelocyTrack never clones your repositories or stores source code files on its servers. We only interact with commit metadata, branch heads, pull request details, and work item fields via official APIs. All pull request threads, branch ahead/behind metrics, and work item insights are rendered transiently in your browser session.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-8 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Is VelocyTrack GDPR compliant and how is my data protected?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-8" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Yes. VelocyTrack adheres to strict European Union data protection standards and full <strong>GDPR (General Data Protection Regulation)</strong> compliance:
                                        <ul class="mt-2 mb-0 pl-3">
                                            <li class="mb-1"><strong>EU Data Sovereignty:</strong> Hosted exclusively on dedicated infrastructure in the European Union with tenant workspace segregation.</li>
                                            <li class="mb-1"><strong>Zero Data Monetization &amp; No AI Training:</strong> We never sell personal data, share records with third-party tracking brokers, or train AI models on your private source code, commit history, or backlog tasks.</li>
                                            <li class="mb-1"><strong>Enterprise Encryption:</strong> All Personal Access Tokens (PATs) and credentials are encrypted at rest using AES-256 and transmitted exclusively over TLS 1.3.</li>
                                            <li><strong>Self-Service Erasure &amp; Portability:</strong> You maintain full ownership of your data with on-demand CSV/Excel export (Right to Data Portability) and instant, irreversible account deletion (Right to Erasure under GDPR Article 17) directly from your profile settings.</li>
                                        </ul>
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-9 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Which platforms will be supported next?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-9" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Azure DevOps is fully supported in Beta. Native integrations for GitHub, GitLab, and Jira Software are coming next. Select your preferred toolchain in the waitlist form above to prioritize your platform's rollout.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-10 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>How does VelocyTrack differ from native Azure DevOps extensions (like 7pace)?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-10" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Traditional marketplace extensions run inside sluggish iframes inside single work item modals, lack multi-repo PR and branch intelligence, and charge high per-seat premiums. VelocyTrack operates as a standalone, ultra-fast (&lt;3s time entry) engineering cockpit. It aggregates multiple repositories, active branches, PR discussions, and automated capacity across your entire sprint without making you click through dozens of nested Azure DevOps tabs.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-11 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Do I need Organization Administrator permissions in Azure DevOps to connect?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-11" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        No. VelocyTrack only requires personal read/write access to the specific projects and repositories you contribute to via standard Personal Access Tokens (PAT) or OAuth. You do not need tenant-wide Organization Administrator approval, org-level marketplace installation, or elevated cloud permissions.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>

                            <b-card no-body class="mb-2 shadow-sm border-0 faq-item">
                                <b-card-header header-tag="header" class="p-0 border-0" role="tab">
                                    <b-button block v-b-toggle.faq-12 variant="white" class="text-left font-weight-bold py-3 px-4 d-flex justify-content-between align-items-center">
                                        <span>Can I export or delete my data if my team stops using VelocyTrack?</span>
                                        <i class="bi bi-chevron-down faq-chevron"></i>
                                    </b-button>
                                </b-card-header>
                                <b-collapse id="faq-12" accordion="faq-accordion" role="tabpanel">
                                    <b-card-body class="pt-0 text-muted">
                                        Yes. All time entries logged in VelocyTrack are written directly to your Azure DevOps work items, so your historical time records remain permanently in your organization's own system. You can also export your full timesheet history to CSV or Excel at any time. If you wish to delete your account and stored credentials, you can trigger instant account deletion from your profile or contact privacy@velocytrack.com.
                                    </b-card-body>
                                </b-collapse>
                            </b-card>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Footer -->
        <footer class="landing-footer py-5 border-top">
            <div class="container">
                <div class="row align-items-center mb-4">
                    <div class="col-md-5 text-center text-md-left mb-3 mb-md-0">
                        <div class="d-flex align-items-center justify-content-center justify-content-md-start">
                            <img :src="logoUrl" alt="VelocyTrack Logo" class="brand-logo-img mr-2" />
                            <span class="font-weight-bold text-dark h5 mb-0">VelocyTrack</span>
                        </div>
                        <p class="text-muted small mt-2 mb-0">
                            Developer time tracking and sprint capacity built directly into your engineering backlog.
                        </p>
                        <div class="mt-3 d-flex flex-wrap align-items-center justify-content-center justify-content-md-start">
                            <a
                                href="https://peerlist.io/jemyto/project/velocytrack"
                                target="_blank"
                                rel="noreferrer"
                                title="VelocyTrack on Peerlist"
                                data-test="link-peerlist-footer-badge"
                                class="platform-badge-link mr-2 mb-2"
                            >
                                <img
                                    src="https://peerlist.io/api/v1/projects/embed/PRJHQ7M68PD99MMNKIMRJRGJ8KRKO6?showUpvote=true&theme=dark"
                                    alt="VelocyTrack"
                                    style="width: auto; height: 72px;"
                                />
                            </a>
                            <a
                                href="https://devhunt.org/tool/velocytrack"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="VelocyTrack on DevHunt"
                                data-test="link-devhunt-footer-badge"
                                class="platform-badge-link mr-2 mb-2"
                            >
                                <img src="https://devhunt.org/badge/velocytrack.svg" alt="VelocyTrack - Featured on DevHunt" width="220" height="54" />
                            </a>
                            <a
                                href="https://www.producthunt.com/products/velocytrack/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-velocytrack"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Review VelocyTrack on Product Hunt"
                                data-test="link-ph-footer-badge"
                                class="platform-badge-link mr-2 mb-2"
                            >
                                <img
                                    src="https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=1340141&theme=dark"
                                    alt="VelocyTrack - Product Hunt"
                                    width="250"
                                    height="54"
                                />
                            </a>
                            <a
                                href="https://betalist.com/startups/velocytrack?utm_campaign=badge-velocytrack&utm_medium=badge&utm_source=badge-featured"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="VelocyTrack on BetaList"
                                data-test="link-betalist-footer-badge"
                                class="platform-badge-link mb-2"
                            >
                                <img
                                    src="https://betalist.com/badges/featured?id=192983&theme=dark"
                                    alt="VelocyTrack - Unify DevOps workflows and time tracking in a developer-first cockpit | BetaList"
                                    style="width: 156px; height: 54px;"
                                    width="156"
                                    height="54"
                                />
                            </a>
                        </div>
                    </div>
                    <div class="col-md-7 text-center text-md-right landing-footer-links">
                        <a href="#pricing" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-pricing" @click.prevent="scrollToSection('pricing')">Pricing &amp; Plans</a>
                        <router-link v-if="showSignIn" to="/sign-in" class="text-muted small px-2 text-decoration-none mr-1" data-test="btn-sign-in-footer">Sign In</router-link>
                        <a href="/privacy" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-privacy" @click.prevent="openLegal('privacy')">Privacy Policy</a>
                        <a href="/terms" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-terms" @click.prevent="openLegal('terms')">Terms of Service</a>
                        <a href="/acceptable-use" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-security" @click.prevent="openLegal('security')">Security &amp; Compliance</a>
                        <a href="mailto:security@velocytrack.com" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-security-contact">Security Contact</a>
                        <a href="/watch" class="text-muted small px-2 text-decoration-none mr-1" data-test="link-footer-video-tour" @click.prevent="openVideoModal">Video Tour</a>
                        <a href="#" class="text-muted small px-2 text-decoration-none" data-test="btn-footer-live-demo" @click.prevent="startInteractiveDemo">Live Demo</a>
                    </div>
                </div>
                <div class="border-top pt-3 text-center text-muted small">
                    &copy; 2026 VelocyTrack. All rights reserved. &bull; Hosted with EU data sovereignty &bull; Payments &amp; Merchant of Record via Creem
                </div>
            </div>
        </footer>
        <!-- Interactive Demo Modal -->
        <b-modal id="demo-signup-modal"
                 v-model="showDemoModal"
                 title="Enter Interactive Demo"
                 centered
                 hide-footer
                 header-close-variant="light"
                 content-class="demo-modal-dark"
                 data-test="modal-demo-signup">
            <div class="p-2">
                <p class="text-muted small mb-3">
                    Experience VelocyTrack with an isolated sandbox environment, live Azure DevOps simulation, and full time-logging capabilities.
                </p>
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="small font-weight-bold text-light">Demo Persona</span>
                    <b-button variant="link" size="sm" class="text-info p-0 small text-decoration-none d-flex align-items-center" data-test="btn-randomize-persona" @click="rollRandomPersona">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                            <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                            <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
                        </svg>
                        Randomize Name
                    </b-button>
                </div>
                <b-form @submit.prevent="launchDemo">
                    <div class="row">
                        <div class="col-6">
                            <b-form-group label="First Name" label-class="small font-weight-bold text-light">
                                <b-form-input v-model="demoForm.firstName" placeholder="First Name" data-test="input-demo-first-name" required></b-form-input>
                            </b-form-group>
                        </div>
                        <div class="col-6">
                            <b-form-group label="Last Name" label-class="small font-weight-bold text-light">
                                <b-form-input v-model="demoForm.lastName" placeholder="Last Name" data-test="input-demo-last-name" required></b-form-input>
                            </b-form-group>
                        </div>
                    </div>
                    <b-form-group label="Email" label-class="small font-weight-bold text-light">
                        <b-input-group append="@demo.velocytrack.com">
                            <b-form-input v-model="demoForm.emailPrefix" placeholder="firstname.lastname" data-test="input-demo-email-prefix" required></b-form-input>
                        </b-input-group>
                    </b-form-group>

                    <div v-if="demoError" class="alert alert-danger py-2 small mb-3" data-test="alert-demo-error">
                        {{ demoError }}
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-4 pt-2 border-top border-secondary">
                        <b-button variant="link" class="text-muted p-0 small text-decoration-none" @click="showDemoModal = false">
                            Cancel
                        </b-button>
                        <b-button type="submit" variant="primary" class="px-4 font-weight-bold" :disabled="isLaunchingDemo" data-test="btn-submit-launch-demo" :data-state="isLaunchingDemo ? 'loading' : 'idle'">
                            <b-spinner small v-if="isLaunchingDemo" class="mr-1"></b-spinner>
                            Launch Demo
                        </b-button>
                    </div>
                </b-form>
            </div>
        </b-modal>

        <!-- Product Walkthrough Video Modal -->
        <b-modal id="demo-video-modal"
                 v-model="showVideoModal"
                 title="VelocyTrack · Product Demo Walkthrough"
                 size="xl"
                 centered
                 hide-footer
                 header-close-variant="light"
                 content-class="demo-modal-dark"
                 data-test="modal-demo-video">
            <div class="p-1">
                <video
                    v-if="showVideoModal"
                    ref="modalVideoPlayer"
                    :poster="previewTimelog"
                    controls
                    autoplay
                    playsinline
                    preload="metadata"
                    class="w-100 rounded shadow-lg d-block"
                    style="max-height: 74vh; background: #020617;"
                    data-test="video-player-demo"
                    @error="onVideoError"
                    @playing="onVideoPlaying"
                    @loadedmetadata="onVideoLoadedMetadata"
                >
                    <source :src="demoVideoUrl" type="video/mp4">
                    <track
                        label="English captions"
                        kind="subtitles"
                        srclang="en"
                        :src="demoVideoVttUrl"
                    >
                    Your browser does not support the video tag.
                </video>
                <div v-if="videoError" class="alert alert-warning mt-2 mb-0 d-flex align-items-center justify-content-between" data-test="alert-video-error">
                    <div>
                        <i class="bi bi-exclamation-triangle-fill mr-2"></i>
                        Could not stream video directly in browser.
                    </div>
                    <a :href="demoVideoUrl" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-dark font-weight-bold ml-3">
                        Open Direct MP4 &nearr;
                    </a>
                </div>
                <div class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-2 border-top border-secondary">
                    <div class="small text-muted mb-2 mb-sm-0">
                        <i class="bi bi-soundwave text-info mr-1"></i>
                        Narrated 9-chapter developer storyline &bull;
                        <a :href="demoVideoUrl" target="_blank" rel="noopener noreferrer" class="text-info font-weight-bold ml-1 text-decoration-none" data-test="link-direct-mp4">
                            Open direct MP4 link &nearr;
                        </a>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button variant="outline-secondary" size="sm" class="mr-2" data-test="btn-close-video-modal" @click="showVideoModal = false">
                            Close
                        </b-button>
                        <b-button variant="primary" size="sm" class="font-weight-bold px-3" data-test="btn-video-modal-launch-demo" @click="showVideoModal = false; launchInstantDemo()">
                            <i class="bi bi-play-circle-fill mr-1"></i> Try Live Sandbox
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Floating Back to Top Button -->
        <button
            v-show="isScrolled"
            type="button"
            class="btn-back-to-top-float"
            data-test="btn-back-to-top"
            aria-label="Back to top"
            title="Back to top"
            @click="scrollToTop"
        >
            <i class="bi bi-arrow-up"></i>
        </button>
    </div>
</template>

<script>
    import {
        BButton,
        BForm,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BFormInvalidFeedback,
        BAlert,
        BSpinner,
        BCard,
        BCardHeader,
        BCardBody,
        BTabs,
        BTab,
        BModal,
        VBToggle
    } from 'bootstrap-vue';
    import eventBus from '../event-bus';
    import { detectUserLocale } from '../localeHelper';
    import mixinAccount from '../mixinAccount';
    import { getLogoUrl } from '../envHelper';
    import SocialProofStrip from './social-proof-strip.vue';
    import GoogleSignIn from './google-sign-in.vue';
    import { PRICING_TIERS, BETA_GUARANTEE } from '../data/pricing';

    const DEMO_PERSONAS = [
        { firstName: 'Jordan', lastName: 'Blake' },
        { firstName: 'Taylor', lastName: 'Reed' },
        { firstName: 'Riley', lastName: 'Chen' },
        { firstName: 'Morgan', lastName: 'Harper' },
        { firstName: 'Casey', lastName: 'Miller' },
        { firstName: 'Avery', lastName: 'Novak' },
        { firstName: 'Jamie', lastName: 'Torres' },
        { firstName: 'Quinn', lastName: 'Bennett' },
        { firstName: 'Samira', lastName: 'Patel' },
        { firstName: 'Lucas', lastName: 'Wright' },
        { firstName: 'Maya', lastName: 'Lin' },
        { firstName: 'Cameron', lastName: 'Cole' },
        { firstName: 'Devon', lastName: 'Hayes' },
        { firstName: 'Skyler', lastName: 'Diaz' },
        { firstName: 'Adrian', lastName: 'Cross' },
        { firstName: 'Rowan', lastName: 'Foster' },
        { firstName: 'Robin', lastName: 'Brooks' },
        { firstName: 'Dakota', lastName: 'Shaw' },
        { firstName: 'Kai', lastName: 'Nakamura' },
        { firstName: 'Leo', lastName: 'Sterling' }
    ];

    function getRandomDemoPersona() {
        const p = DEMO_PERSONAS[Math.floor(Math.random() * DEMO_PERSONAS.length)];
        return {
            firstName: p.firstName,
            lastName: p.lastName,
            emailPrefix: `${p.firstName.toLowerCase()}.${p.lastName.toLowerCase()}`
        };
    }

    export default {
        name: 'PageLanding',
        mixins: [mixinAccount],
        components: {
            BButton,
            BForm,
            BFormGroup,
            BFormInput,
            BFormSelect,
            BFormInvalidFeedback,
            BAlert,
            BSpinner,
            BCard,
            BCardHeader,
            BCardBody,
            BTabs,
            BTab,
            BModal,
            SocialProofStrip,
            GoogleSignIn
        },
        directives: {
            'b-toggle': VBToggle
        },
        data() {
            return {
                isScrolled: false,
                activeSection: '',
                isLaunchingInstantDemo: false,
                activeHeroTab: 'timelog',
                pricingTiers: PRICING_TIERS,
                betaGuarantee: BETA_GUARANTEE,
                minutesLostPerDay: 20,
                hourlyRate: 60,
                waitlistStatus: {
                    isOpen: true,
                    socialProof: null,
                    totalRegistered: 0,
                    spotsRemaining: 0,
                    maxCapacity: 0,
                    isFull: false
                },
                claimResult: {
                    status: '',
                    message: '',
                    token: null
                },
                form: {
                    email: '',
                    fullName: '',
                    role: 'Software Engineer / Developer',
                    platforms: ['Azure DevOps'],
                    website: ''
                },
                roleOptions: [
                    { value: 'Software Engineer / Developer', text: 'Software Engineer / Developer' },
                    { value: 'Tech Lead / Architect', text: 'Tech Lead / Architect' },
                    { value: 'QA / Test Automation', text: 'QA / Test Automation (Coming Next)', disabled: true },
                    { value: 'System Analyst', text: 'System Analyst (Coming Next)', disabled: true }
                ],
                availablePlatforms: [
                    { id: 'Azure DevOps', name: 'Azure DevOps', icon: 'icon-azure', color: '#0078d4', phase: 'Live Beta', status: 'Live in Beta', isSupported: true },
                    { id: 'GitHub', name: 'GitHub', icon: 'fa-brands fa-github', color: '#f8fafc', phase: 'Coming Next', status: 'In Roadmap', isSupported: false },
                    { id: 'GitLab', name: 'GitLab', icon: 'fa-brands fa-gitlab', color: '#fc6d26', phase: 'Coming Next', status: 'In Roadmap', isSupported: false },
                    { id: 'Jira', name: 'Jira Software', icon: 'fa-brands fa-jira', color: '#2684ff', phase: 'Coming Next', status: 'In Roadmap', isSupported: false }
                ],
                isSubmitting: false,
                registrationSuccess: false,
                registrationPosition: 0,
                errorMessage: '',
                launchEmail: '',
                launchWebsite: '',
                isLaunchSubmitting: false,
                launchSuccess: false,
                launchSuccessMessage: '',
                launchError: '',
                googleClientId: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                showDemoModal: false,
                showVideoModal: false,
                videoError: false,
                demoVideoDurationShort: '4:39',
                demoVideoDurationLabel: '4m 39s',
                isLaunchingDemo: false,
                demoError: '',
                demoForm: getRandomDemoPersona(),
                turnstileToken: null,
                turnstileWidgetId: null,
                testimonials: [],
                loadingTestimonials: false,
                hasInvite: false,
                inviteToken: null,
                showSignIn: true
            };
        },
        computed: {
            signInLink() {
                return '/sign-in';
            },
            logoUrl() {
                return getLogoUrl(this.currentEnvironment);
            },
            demoVideoUrl() {
                const version = process.env.VUE_APP_GIT_COMMIT || 'v2';
                return `${process.env.BASE_URL || '/'}videos/velocytrack-demo.mp4?v=${version}`;
            },
            demoVideoVttUrl() {
                const version = process.env.VUE_APP_GIT_COMMIT || 'v2';
                return `${process.env.BASE_URL || '/'}videos/velocytrack-demo.vtt?v=${version}`;
            },
            previewTimelog() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-timelog.png`;
            },
            previewBacklog() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-backlog.png`;
            },
            previewPullRequests() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-pull-requests.png`;
            },
            previewBranches() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-branches.png`;
            },
            previewDiscussions() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-discussions.png`;
            },
            percentClaimed() {
                if (!this.waitlistStatus.maxCapacity) return 0;
                const pct = Math.round((this.waitlistStatus.totalRegistered / this.waitlistStatus.maxCapacity) * 100);
                return Math.min(100, Math.max(0, pct));
            },
            hoursSavedPerSprint() {
                return Math.round((this.minutesLostPerDay * 10 / 60) * 10) / 10;
            },
            annualHoursSaved() {
                return Math.round(this.minutesLostPerDay * 230 / 60);
            },
            annualValueSaved() {
                return Math.round((this.minutesLostPerDay * 230 / 60) * this.hourlyRate).toLocaleString('en-US');
            },
            isDevHuntLive() {
                const queryOverride = this.$route && this.$route.query ? this.$route.query.devhunt_live : undefined;
                if (queryOverride !== undefined) {
                    return queryOverride === '1' || queryOverride === 'true';
                }
                const now = new Date();
                // DevHunt Tool of the Week Contest: active for 1 full week starting October 20, 2026 UTC
                const launchStart = new Date('2026-10-20T00:00:00Z');
                const launchEnd = new Date('2026-10-27T23:59:59.999Z');
                return now >= launchStart && now <= launchEnd;
            }
        },
        watch: {
            '$route.query': {
                immediate: true,
                handler() {
                    this.detectInvite();
                }
            },
            '$route.path'() {
                this.checkAutoLaunchDemo();
            }
        },
        mounted() {
            this.detectInvite();
            this.checkAutoLaunchDemo();
            this.fetchWaitlistStatus();
            this.loadTestimonials();
            this.probeDemoVideoDuration();
            this.handleScroll();
            window.addEventListener('scroll', this.handleScroll, { passive: true });
        },
        beforeUnmount() {
            window.removeEventListener('scroll', this.handleScroll);
            if (window.turnstile && this.turnstileWidgetId) {
                try {
                    window.turnstile.remove(this.turnstileWidgetId);
                } catch (e) {
                    // Ignore cleanup error
                }
            }
        },
        methods: {
            openVideoModal() {
                this.videoError = false;
                this.showVideoModal = true;
                this.$nextTick(() => {
                    this.syncCaptionPreference();
                });
            },
            onVideoPlaying() {
                this.videoError = false;
                this.syncCaptionPreference();
            },
            onVideoLoadedMetadata() {
                this.syncCaptionPreference();
            },
            syncCaptionPreference() {
                const video = this.$refs.modalVideoPlayer;
                if (!video || !video.textTracks) return;

                let pref = null;
                try {
                    pref = localStorage.getItem('vt_demo_captions');
                } catch (e) {
                    // Ignore storage access error
                }

                // Subtitles default to OFF unless user explicitly enabled them in player options
                const shouldShow = pref === '1';
                for (let i = 0; i < video.textTracks.length; i++) {
                    video.textTracks[i].mode = shouldShow ? 'showing' : 'disabled';
                }

                if (!video.__captionListenerBound) {
                    video.__captionListenerBound = true;
                    video.textTracks.onchange = () => {
                        const isShowing = Array.from(video.textTracks).some(t => t.mode === 'showing');
                        try {
                            localStorage.setItem('vt_demo_captions', isShowing ? '1' : '0');
                        } catch (e) {
                            // Ignore storage access error
                        }
                    };
                }
            },
            onVideoError() {
                this.videoError = true;
            },
            probeDemoVideoDuration() {
                if (typeof document === 'undefined') return;
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => {
                    const totalSec = Math.round(video.duration || 0);
                    if (totalSec > 0 && isFinite(totalSec)) {
                        const mins = Math.floor(totalSec / 60);
                        const secs = totalSec % 60;
                        this.demoVideoDurationShort = `${mins}:${secs < 10 ? '0' : ''}${secs}`;
                        this.demoVideoDurationLabel = `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
                    }
                };
                video.src = this.demoVideoUrl;
            },
            checkAutoLaunchDemo() {
                const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
                if (/googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|crawler|spider/i.test(ua)) {
                    return;
                }
                const isDemoRoute = !!(this.$route && this.$route.meta && this.$route.meta.autoLaunchDemo);
                const demoQuery = this.$route && this.$route.query ? this.$route.query.demo : undefined;
                const isDemoQuery = demoQuery === '1' || demoQuery === 'true' || demoQuery === 'instant' || demoQuery === '';
                if (isDemoRoute || isDemoQuery) {
                    this.launchInstantDemo();
                }
            },
            detectInvite() {
                try {
                    localStorage.removeItem('velocytrack_invite_token');
                } catch (e) {
                    // Ignore localStorage errors
                }
                const queryToken = (this.$route.query.token || this.$route.query.invite || '').toString().trim();
                if (queryToken) {
                    this.$router.push(`/sign-up/${encodeURIComponent(queryToken)}`);
                }
            },
            handleScroll() {
                const scrollY = window.scrollY || window.pageYOffset || 0;
                this.isScrolled = scrollY > 60;

                const sections = ['capabilities', 'compare', 'testimonials', 'security', 'pricing', 'faq'];
                const headerOffset = 110;
                let current = '';
                for (const id of sections) {
                    const el = document.getElementById(id);
                    if (el) {
                        const top = el.offsetTop - headerOffset;
                        const height = el.offsetHeight;
                        if (scrollY >= top && scrollY < top + height) {
                            current = id;
                        }
                    }
                }
                this.activeSection = current;
            },
            async launchInstantDemo() {
                if (this.isLaunchingInstantDemo || this.isLaunchingDemo) return;
                this.isLaunchingInstantDemo = true;
                this.demoError = '';
                this.rollRandomPersona();
                try {
                    const detected = detectUserLocale(this.$store?.state?.countryCode || 'BG');
                    this.$store.commit('setCountryCode', detected.countryCode);
                    this.$store.commit('setTimezone', detected.timezone);

                    const resp = await this.$http.post('/Demo/StartSession', {
                        firstName: this.demoForm.firstName,
                        lastName: this.demoForm.lastName,
                        email: `${this.demoForm.emailPrefix}@demo.velocytrack.com`,
                        countryCode: detected.countryCode,
                        timezone: detected.timezone
                    });
                    if (resp.data && resp.data.account) {
                        this.$store.commit('setTheme', 'dark');
                        document.documentElement.setAttribute('data-theme', 'dark');
                        this.$store.commit('setAccount', resp.data.account);
                        this.$store.commit('setAuthenticated', true);
                        this.showDemoModal = false;
                        await this.initSessionConfigAsync();
                        const targetUrl = resp.data.redirectUrl || '/timelog';
                        if (this.$route.path !== targetUrl) {
                            await this.$router.push(targetUrl).catch(() => {});
                        }
                    } else {
                        this.startInteractiveDemo();
                    }
                } catch (err) {
                    this.startInteractiveDemo();
                } finally {
                    this.isLaunchingInstantDemo = false;
                }
            },
            initTurnstile() {
                if (this.turnstileWidgetId !== null) return;
                if (!window.turnstile && !document.getElementById('cf-turnstile-script')) {
                    const s = document.createElement('script');
                    s.id = 'cf-turnstile-script';
                    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                    s.async = true;
                    s.defer = true;
                    document.head.appendChild(s);
                }
                const interval = setInterval(() => {
                    if (window.turnstile && this.$refs.turnstileWaitlistContainer) {
                        clearInterval(interval);
                        if (this.turnstileWidgetId !== null) return;
                        try {
                            this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileWaitlistContainer, {
                                sitekey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAFMGqfq985aa_XZc',
                                theme: 'dark',
                                action: 'waitlist',
                                callback: (token) => {
                                    this.turnstileToken = token;
                                },
                                'expired-callback': () => {
                                    this.turnstileToken = null;
                                },
                                'error-callback': () => {
                                    this.turnstileToken = null;
                                }
                            });
                        } catch (e) {
                            // Widget already rendered
                        }
                    }
                }, 200);
            },
            resetTurnstile() {
                if (window.turnstile && this.turnstileWidgetId) {
                    try {
                        window.turnstile.reset(this.turnstileWidgetId);
                    } catch (e) {
                        // Ignore reset error
                    }
                }
                this.turnstileToken = null;
            },
            getAttributionSource() {
                try {
                    const q = (this.$route && this.$route.query) || {};
                    const utmSource = (q.utm_source || q.ref || q.source || '').toString().trim();
                    if (utmSource) {
                        const details = [q.utm_medium, q.utm_campaign].map(v => (v || '').toString().trim()).filter(Boolean).join(' / ');
                        return (details ? `${utmSource} (${details})` : utmSource).substring(0, 150);
                    }
                    const stored = sessionStorage.getItem('vt_attribution') || localStorage.getItem('vt_attribution');
                    if (stored) {
                        return stored.substring(0, 150);
                    }
                } catch (e) {
                    // Ignore storage access errors
                }
                return null;
            },
            async fetchWaitlistStatus() {
                try {
                    const resp = await this.$http.get('/Waitlist/Status');
                    if (resp.data) {
                        this.waitlistStatus = resp.data;
                    }
                } catch (e) {
                    // Fallback to default optimistic status if backend is starting
                }
            },
            loginOAuth(provider) {
                const returnUrl = encodeURIComponent(window.location.origin);
                let url = `${this.$http.defaults.baseURL}/Account/ExternalLogin?provider=${provider}&returnUrl=${returnUrl}&prompt=select_account`;
                if (this.claimResult && this.claimResult.token) {
                    url += `&token=${encodeURIComponent(this.claimResult.token)}`;
                }
                window.location.href = url;
            },
            async onQuickStartClaimPending(payload) {
                this.errorMessage = '';
                try {
                    const resp = await this.$http.post('/Account/ClaimBetaFromSso', {
                        email: payload?.email
                    });
                    const data = resp.data;
                    if (data && data.status === 'signedIn' && data.account) {
                        this.$store.commit('setAccount', data.account);
                        this.$store.commit('setAuthenticated', true);
                        await this.initSessionConfigAsync();
                        this.$router.push('/timelog').catch(() => {});
                        return;
                    }
                    this.errorMessage = data?.message || 'Unable to complete instant Google sign-in. Please use the invitation link sent to your email.';
                } catch (err) {
                    const respData = err.response?.data;
                    this.errorMessage = respData?.message || (typeof respData === 'string' ? respData : null) || 'Unable to complete instant Google sign-in.';
                }
            },
            onQuickStartSuccess() {
                this.$router.push('/timelog').catch(() => {});
            },
            onQuickStartError(msg) {
                const detail = typeof msg === 'string' ? msg : (msg?.message || 'Sign in failed.');
                this.errorMessage = `${detail} Make sure you pick the account that uses ${this.form.email}, or use the invitation link we emailed you.`;
            },
            async onRegister() {
                this.isSubmitting = true;
                this.errorMessage = '';
                try {
                    const payload = {
                        email: this.form.email,
                        fullName: this.form.fullName,
                        role: this.form.role,
                        platform: 'Azure DevOps',
                        website: this.form.website || null,
                        turnstileToken: this.turnstileToken,
                        source: this.getAttributionSource()
                    };
                    const resp = await this.$http.post('/Waitlist/Join', payload);
                    if (resp.data && resp.data.success) {
                        this.registrationSuccess = true;
                        this.claimResult = {
                            status: resp.data.status || 'queued',
                            message: resp.data.message || '',
                            token: resp.data.token || null
                        };
                        this.registrationPosition = resp.data.position || 0;
                        await this.fetchWaitlistStatus();
                    } else {
                        this.errorMessage = resp.data?.message || 'Failed to claim beta spot. Please try again.';
                        this.resetTurnstile();
                    }
                } catch (err) {
                    this.errorMessage = err.response?.data?.message || 'Network error while contacting waitlist service.';
                    this.resetTurnstile();
                } finally {
                    this.isSubmitting = false;
                }
            },
            scrollToTop() {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            },
            scrollToSection(id) {
                const el = document.getElementById(id);
                if (el) {
                    const header = document.querySelector('header.landing-header');
                    const headerHeight = header ? header.offsetHeight : 54;
                    const targetPosition = el.getBoundingClientRect().top + window.pageYOffset - headerHeight - 10;
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            },
            scrollToLaunch() {
                const el = document.getElementById('launch-notify');
                if (el) {
                    const header = document.querySelector('header.landing-header');
                    const headerHeight = header ? header.offsetHeight : 54;
                    const targetPosition = el.getBoundingClientRect().top + window.pageYOffset - headerHeight - 10;
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            },
            async onSubscribeLaunchAsync() {
                if (!this.launchEmail || !this.launchEmail.trim()) {
                    this.launchError = 'Please enter a valid work email address.';
                    return;
                }
                this.isLaunchSubmitting = true;
                this.launchError = '';
                this.launchSuccess = false;
                try {
                    const payload = {
                        email: this.launchEmail.trim(),
                        website: this.launchWebsite || null,
                        turnstileToken: null,
                        source: this.getAttributionSource()
                    };
                    const resp = await this.$http.post('/Waitlist/SubscribeLaunch', payload);
                    if (resp.data && resp.data.success) {
                        this.launchSuccess = true;
                        this.launchSuccessMessage = resp.data.message || "You're all set! We'll notify you as soon as VelocyTrack officially launches.";
                        this.launchEmail = '';
                    } else {
                        this.launchError = resp.data?.message || 'Failed to subscribe. Please try again.';
                    }
                } catch (err) {
                    this.launchError = err.response?.data?.message || 'Network error while registering subscription. Please try again.';
                } finally {
                    this.isLaunchSubmitting = false;
                }
            },
            rollRandomPersona() {
                const p = getRandomDemoPersona();
                this.demoForm.firstName = p.firstName;
                this.demoForm.lastName = p.lastName;
                this.demoForm.emailPrefix = p.emailPrefix;
            },
            startInteractiveDemo() {
                this.demoError = '';
                this.rollRandomPersona();
                this.showDemoModal = true;
            },
            async launchDemo() {
                this.isLaunchingDemo = true;
                this.demoError = '';
                try {
                    const detected = detectUserLocale(this.$store?.state?.countryCode || 'BG');
                    this.$store.commit('setCountryCode', detected.countryCode);
                    this.$store.commit('setTimezone', detected.timezone);

                    const resp = await this.$http.post('/Demo/StartSession', {
                        firstName: this.demoForm.firstName,
                        lastName: this.demoForm.lastName,
                        email: `${this.demoForm.emailPrefix}@demo.velocytrack.com`,
                        countryCode: detected.countryCode,
                        timezone: detected.timezone
                    });
                    if (resp.data && resp.data.account) {
                        this.$store.commit('setTheme', 'dark');
                        document.documentElement.setAttribute('data-theme', 'dark');
                        this.$store.commit('setAccount', resp.data.account);
                        this.$store.commit('setAuthenticated', true);
                        this.showDemoModal = false;
                        await this.initSessionConfigAsync();
                        const targetUrl = resp.data.redirectUrl || '/timelog';
                        if (this.$route.path !== targetUrl) {
                            await this.$router.push(targetUrl).catch(() => {});
                        }
                    } else {
                        this.demoError = 'Failed to start demo session. Please try again.';
                    }
                } catch (err) {
                    this.demoError = err.response?.data?.message || 'Error connecting to demo service. Please try again.';
                } finally {
                    this.isLaunchingDemo = false;
                }
            },
            scrollToWaitlist() {
                const el = document.getElementById('waitlist');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                }
            },
            openLegal(tab) {
                eventBus.$emit('open-legal', tab);
            },
            async loadTestimonials() {
                this.loadingTestimonials = true;
                try {
                    const res = await this.$http.get('/Feedback/BestReviews');
                    if (Array.isArray(res.data)) {
                        this.testimonials = res.data;
                    } else {
                        this.testimonials = [];
                    }
                } catch (e) {
                    this.testimonials = [];
                } finally {
                    this.loadingTestimonials = false;
                }
            },
            getTestimonialBadgeClass(idx) {
                const classes = ['badge-metric-emerald', 'badge-metric-cyan', 'badge-metric-amber'];
                return classes[idx % classes.length];
            },
            getAuthorAvatarClass(idx) {
                const classes = ['avatar-cyan', 'avatar-purple', 'avatar-amber'];
                return classes[idx % classes.length];
            }
        }
    };
</script>

<style scoped>
    .landing-page-container {
        font-family: inherit;
        color: #1e293b;
        background-color: #030712;
        overflow-x: clip;
        width: 100%;
        position: relative;
    }

    .brand-logo-img {
        width: 34px;
        height: 34px;
        object-fit: contain;
        vertical-align: middle;
        flex-shrink: 0;
        border-radius: 6px;
        box-shadow: 0 0 15px rgba(56, 189, 248, 0.3);
        transition: all 0.25s ease;
    }

    .brand-name {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        background: linear-gradient(135deg, #ffffff 40%, #94a3b8 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        transition: all 0.25s ease;
    }

    .beta-pill {
        font-size: 0.7rem;
        letter-spacing: 0.5px;
        font-weight: 700;
        vertical-align: middle;
        background: linear-gradient(135deg, #0284c7 0%, #6366f1 100%);
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .landing-header {
        background-color: rgba(3, 7, 18, 0.85);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
        position: sticky;
        top: 0;
        z-index: 1030;
        transition: padding 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    }

    .landing-nav-links {
        gap: 0.35rem;
    }

    .landing-nav-link {
        color: #94a3b8;
        font-size: 0.84rem;
        font-weight: 500;
        text-decoration: none;
        padding: 0.25rem 0.65rem;
        border-radius: 9999px;
        transition: all 0.18s ease;
        border: 1px solid transparent;
        white-space: nowrap;
    }

    .landing-nav-link:hover {
        color: #ffffff;
        background-color: rgba(255, 255, 255, 0.08);
        text-decoration: none;
    }

    .landing-nav-link.active {
        color: #38bdf8;
        background-color: rgba(56, 189, 248, 0.12);
        border-color: rgba(56, 189, 248, 0.3);
        font-weight: 600;
        text-decoration: none;
    }

    section[id] {
        scroll-margin-top: 65px;
    }

    /* Hero Section with Aceternity Ambient Glow & Dot-Matrix */
    .landing-hero {
        background-color: #030712;
        background-image: 
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(56, 189, 248, 0.22), rgba(99, 102, 241, 0.14) 50%, transparent 80%),
            radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 1px);
        background-size: 100% 100%, 28px 28px;
        background-position: center top, center center;
        padding: 5.5rem 1rem 4.5rem 1rem;
        color: #ffffff;
        position: relative;
        overflow: hidden;
    }

    .hero-content-layer {
        z-index: 2;
    }

    .hero-title {
        font-size: clamp(1.65rem, 5.5vw, 3.6rem);
        letter-spacing: -0.035em;
        line-height: 1.18;
        overflow-wrap: break-word;
    }

    .hero-title-main {
        background: linear-gradient(180deg, #FFFFFF 0%, #cbd5e1 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .text-gradient-cyan {
        background: linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .hero-subtitle {
        max-width: 720px;
        font-size: 1.12rem;
        line-height: 1.65;
        color: #94a3b8;
    }

    .text-light-muted {
        color: #94a3b8;
    }

    .hover-underline:hover {
        text-decoration: underline !important;
    }

    .waitlist-invite-note {
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    /* Aceternity Style Pill / Badge */
    .aceternity-pill {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow: 0 0 20px -2px rgba(56, 189, 248, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-radius: 9999px;
        color: #f1f5f9;
        font-size: 0.82rem;
        letter-spacing: 0.02em;
    }

    .pulse-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #10b981;
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
        animation: pulse 1.6s infinite;
    }

    @keyframes pulse {
        0% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
        }
        70% {
            box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
        }
        100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
        }
    }

    /* Aceternity Radiant CTA Buttons */
    .btn-aceternity-primary {
        background: linear-gradient(135deg, #0284c7 0%, #4f46e5 100%);
        border: 1px solid rgba(255, 255, 255, 0.22);
        color: #ffffff !important;
        border-radius: 10px;
        font-weight: 600;
        letter-spacing: -0.01em;
        box-shadow: 0 0 24px -4px rgba(79, 70, 229, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2);
        transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .btn-aceternity-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 0 35px -2px rgba(56, 189, 248, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.35);
    }

    .btn-aceternity-primary:active {
        transform: translateY(0);
    }

    .btn-aceternity-secondary {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.15);
        color: #f1f5f9 !important;
        border-radius: 10px;
        font-weight: 600;
        letter-spacing: -0.01em;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
        transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .btn-aceternity-secondary:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.3);
        color: #ffffff !important;
        transform: translateY(-2px);
        box-shadow: 0 0 20px -2px rgba(255, 255, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }

    .btn-aceternity-secondary:active {
        transform: translateY(0);
    }

    /* Hero Video Frame CTA Button */
    .hero-video-frame-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.35rem 0.95rem 0.35rem 0.45rem;
        background: linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.85));
        border: 1px solid rgba(56, 189, 248, 0.38);
        border-radius: 12px;
        color: #f8fafc;
        cursor: pointer;
        box-shadow: 0 8px 24px -6px rgba(2, 132, 199, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.14);
        transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-video-frame-btn:hover,
    .hero-video-frame-btn:focus {
        outline: none;
        transform: translateY(-2px);
        border-color: rgba(56, 189, 248, 0.75);
        background: linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(30, 58, 138, 0.55));
        box-shadow: 0 12px 28px -4px rgba(56, 189, 248, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    }

    .hero-video-frame-btn:active {
        transform: translateY(0);
    }

    .video-frame-thumb {
        position: relative;
        width: 68px;
        height: 42px;
        border-radius: 7px;
        background:
            radial-gradient(circle at 50% 45%, rgba(56, 189, 248, 0.28), rgba(15, 23, 42, 0.96) 72%),
            repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0px, rgba(255, 255, 255, 0.04) 1px, transparent 1px, transparent 10px);
        border: 1px solid rgba(148, 163, 184, 0.32);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
        box-shadow: inset 0 0 12px rgba(0, 0, 0, 0.65);
    }

    .video-frame-window-dots {
        position: absolute;
        top: 4px;
        left: 5px;
        display: inline-flex;
        gap: 3px;
    }

    .video-frame-window-dots span {
        width: 4px;
        height: 4px;
        border-radius: 50%;
    }

    .video-frame-window-dots span:nth-child(1) { background: #ef4444; }
    .video-frame-window-dots span:nth-child(2) { background: #f59e0b; }
    .video-frame-window-dots span:nth-child(3) { background: #10b981; }

    .video-frame-play-circle {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: linear-gradient(135deg, #38bdf8 0%, #4f46e5 100%);
        color: #ffffff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        padding-left: 2px;
        box-shadow: 0 0 12px rgba(56, 189, 248, 0.75);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .hero-video-frame-btn:hover .video-frame-play-circle {
        transform: scale(1.12);
        box-shadow: 0 0 16px rgba(56, 189, 248, 0.95);
    }

    .video-frame-duration {
        position: absolute;
        right: 4px;
        bottom: 5px;
        font-size: 0.56rem;
        font-weight: 700;
        line-height: 1;
        padding: 1.5px 3.5px;
        border-radius: 3px;
        background: rgba(2, 6, 23, 0.88);
        color: #e2e8f0;
        letter-spacing: 0.02em;
    }

    .video-frame-scrubber {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 3px;
        background: rgba(255, 255, 255, 0.16);
    }

    .video-frame-scrubber-fill {
        display: block;
        width: 42%;
        height: 100%;
        background: linear-gradient(90deg, #38bdf8, #818cf8);
        transition: width 0.3s ease;
    }

    .hero-video-frame-btn:hover .video-frame-scrubber-fill {
        width: 68%;
    }

    .video-frame-info {
        display: flex;
        flex-direction: column;
        line-height: 1.18;
    }

    .video-frame-title {
        font-size: 0.98rem;
        font-weight: 700;
        color: #ffffff;
        letter-spacing: -0.01em;
    }

    .video-frame-subtitle {
        font-size: 0.70rem;
        font-weight: 600;
        color: #94a3b8;
        margin-top: 2px;
    }

    .aceternity-chip-badge {
        display: inline-flex;
        align-items: center;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 9999px;
        color: #cbd5e1;
        font-size: 0.8rem;
        font-weight: 500;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }

    /* Core Platform Capabilities Section */
    .landing-capabilities-section {
        background-color: #030712;
        background-image: 
            radial-gradient(circle at 50% 0%, rgba(56, 189, 248, 0.08), transparent 50%),
            radial-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px);
        background-size: 100% 100%, 28px 28px;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        padding: 5rem 1rem 3.5rem 1rem;
    }

    .section-heading {
        font-size: clamp(1.8rem, 3.5vw, 2.5rem);
        letter-spacing: -0.03em;
    }

    .section-subheading {
        max-width: 640px;
        color: #94a3b8;
        font-size: 1.05rem;
        line-height: 1.6;
    }

    .capability-card {
        padding: 2.25rem 2rem;
        background: rgba(15, 23, 42, 0.7);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .capability-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 140px;
        opacity: 0.6;
        transition: opacity 0.3s ease;
        pointer-events: none;
    }

    .capability-card:hover::before {
        opacity: 1;
    }

    .capability-cyan::before {
        background: radial-gradient(circle at 18% 0%, rgba(56, 189, 248, 0.16), transparent 70%);
    }

    .capability-emerald::before {
        background: radial-gradient(circle at 18% 0%, rgba(52, 211, 153, 0.16), transparent 70%);
    }

    .capability-amber::before {
        background: radial-gradient(circle at 18% 0%, rgba(251, 191, 36, 0.16), transparent 70%);
    }

    .capability-purple::before {
        background: radial-gradient(circle at 18% 0%, rgba(167, 139, 250, 0.16), transparent 70%);
    }

    .capability-card:hover {
        transform: translateY(-4px);
        border-color: rgba(255, 255, 255, 0.2);
    }

    .capability-cyan:hover {
        box-shadow: 0 20px 40px -15px rgba(56, 189, 248, 0.25), inset 0 1px 0 rgba(56, 189, 248, 0.3);
        border-color: rgba(56, 189, 248, 0.35);
    }

    .capability-emerald:hover {
        box-shadow: 0 20px 40px -15px rgba(52, 211, 153, 0.25), inset 0 1px 0 rgba(52, 211, 153, 0.3);
        border-color: rgba(52, 211, 153, 0.35);
    }

    .capability-amber:hover {
        box-shadow: 0 20px 40px -15px rgba(251, 191, 36, 0.25), inset 0 1px 0 rgba(251, 191, 36, 0.3);
        border-color: rgba(251, 191, 36, 0.35);
    }

    .capability-purple:hover {
        box-shadow: 0 20px 40px -15px rgba(167, 139, 250, 0.25), inset 0 1px 0 rgba(167, 139, 250, 0.3);
        border-color: rgba(167, 139, 250, 0.35);
    }

    .capability-header {
        display: flex;
        align-items: center;
        margin-bottom: 1.25rem;
    }

    .capability-icon-wrap {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        margin-right: 1.15rem;
        flex-shrink: 0;
    }

    .capability-cyan .capability-icon-wrap {
        background: rgba(56, 189, 248, 0.12);
        border-color: rgba(56, 189, 248, 0.25);
    }

    .capability-emerald .capability-icon-wrap {
        background: rgba(52, 211, 153, 0.12);
        border-color: rgba(52, 211, 153, 0.25);
    }

    .capability-amber .capability-icon-wrap {
        background: rgba(251, 191, 36, 0.12);
        border-color: rgba(251, 191, 36, 0.25);
    }

    .capability-purple .capability-icon-wrap {
        background: rgba(167, 139, 250, 0.12);
        border-color: rgba(167, 139, 250, 0.25);
    }

    .capability-icon {
        font-size: 1.65rem;
    }

    .text-cyan {
        color: #38bdf8 !important;
    }

    .text-emerald {
        color: #34d399 !important;
    }

    .text-amber {
        color: #fbbf24 !important;
    }

    .text-purple {
        color: #a78bfa !important;
    }

    .capability-title {
        font-size: 1.3rem;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.025em;
        margin-bottom: 0;
    }

    .capability-description {
        font-size: 0.98rem;
        line-height: 1.7;
        color: #94a3b8;
    }

    /* Section Styling */
    .bg-surface {
        background-color: #f8fafc;
    }

    .feature-card {
        border-radius: 14px;
    }

    .feature-tab-icon {
        font-size: 1.6rem;
    }

    .preview-metric-box {
        background-color: #f1f5f9;
        border: 1px solid #e2e8f0;
    }

    .comparison-table {
        border-radius: 8px;
        overflow: hidden;
    }

    .table-highlight {
        background-color: #f0f7ff;
    }

    .comparison-icon {
        font-size: 1.15rem;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .comparison-cell-value {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        font-size: 0.875rem;
    }

    /* Official Launch Updates Section */
    .landing-launch-section {
        position: relative;
        background-color: #030712;
        background-image: 
            radial-gradient(ellipse 65% 50% at 50% 0%, rgba(6, 182, 212, 0.12), transparent 70%),
            radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        background-size: 100% 100%, 28px 28px;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        padding: 5rem 1rem;
    }

    .launch-notify-card {
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%) !important;
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        border: 1px solid rgba(6, 182, 212, 0.25) !important;
        border-radius: 20px !important;
        box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.8), 0 0 40px -10px rgba(6, 182, 212, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.15);
        color: #f1f5f9;
        position: relative;
    }

    .launch-card-aura {
        position: absolute;
        top: -50%;
        left: 20%;
        right: 20%;
        height: 100%;
        background: radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%);
        pointer-events: none;
        z-index: 0;
    }

    .launch-title {
        font-size: clamp(1.6rem, 3vw, 2.2rem);
        letter-spacing: -0.025em;
        position: relative;
        z-index: 1;
    }

    .launch-subtitle {
        max-width: 620px;
        font-size: 1rem;
        line-height: 1.6;
        position: relative;
        z-index: 1;
    }

    .launch-form {
        max-width: 580px;
        position: relative;
        z-index: 1;
    }

    .launch-input-group {
        background: rgba(3, 7, 18, 0.6);
        padding: 0.35rem;
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4);
    }

    .launch-email-input {
        background: transparent !important;
        border: none !important;
        color: #ffffff !important;
        font-size: 0.95rem;
        padding-left: 1rem;
        box-shadow: none !important;
    }

    .launch-email-input::placeholder {
        color: #64748b;
    }

    .launch-submit-btn {
        border-radius: 8px !important;
        font-size: 0.95rem !important;
        padding: 0.65rem 1.5rem !important;
        white-space: nowrap;
    }

    .launch-perks-row {
        position: relative;
        z-index: 1;
    }

    .launch-perk-item {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        padding: 0.3rem 0.85rem;
    }

    /* Waitlist Section & Form - Aceternity Dark Obsidian */
    .landing-waitlist-section {
        position: relative;
        background-color: #030712;
        background-image: 
            radial-gradient(ellipse 70% 40% at 50% 0%, rgba(99, 102, 241, 0.12), transparent 70%),
            radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
        background-size: 100% 100%, 28px 28px;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        padding: 5rem 1rem;
    }

    .aceternity-card {
        background: rgba(15, 23, 42, 0.8) !important;
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        border-radius: 18px !important;
        box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.15);
        color: #f1f5f9;
    }

    .waitlist-title {
        color: #ffffff;
        font-size: 1.85rem;
        letter-spacing: -0.025em;
    }

    .waitlist-subtitle {
        color: #94a3b8;
        max-width: 580px;
        margin-left: auto;
        margin-right: auto;
        line-height: 1.6;
    }

    .waitlist-capacity-box {
        background: rgba(30, 41, 59, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
    }

    .capacity-label {
        color: #cbd5e1;
    }

    .waitlist-spots-meta {
        color: #94a3b8;
    }

    .aceternity-progress {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .aceternity-success-alert {
        background: rgba(16, 185, 129, 0.15) !important;
        border: 1px solid rgba(16, 185, 129, 0.3) !important;
        color: #6ee7b7 !important;
        border-radius: 12px;
    }

    .waitlist-quickstart {
        max-width: 320px;
    }

    .waitlist-quickstart .sso-btn {
        background-color: #131314;
        border: 1px solid #3c4043;
        color: #e3e3e3;
        font-size: 14px;
        font-weight: 500;
        height: 40px;
        border-radius: 4px;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }

    .waitlist-quickstart .sso-btn:hover {
        background-color: #202124;
        border-color: #5f6368;
        color: #ffffff;
    }

    .field-label {
        color: #cbd5e1 !important;
        font-size: 0.82rem;
        letter-spacing: 0.02em;
        text-transform: uppercase;
    }

    .aceternity-input,
    .aceternity-select {
        background-color: rgba(2, 6, 23, 0.7) !important;
        border: 1px solid rgba(255, 255, 255, 0.12) !important;
        color: #ffffff !important;
        border-radius: 10px !important;
        padding: 0.65rem 1rem;
        height: auto;
        transition: all 0.2s ease;
    }

    .aceternity-select {
        background-image: var(--vt-select-icon, url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%23cbd5e1' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.85rem center !important;
        background-size: 8px 10px !important;
        padding-right: 2.25rem !important;
    }

    .aceternity-select:hover:not(:disabled) {
        border-color: rgba(56, 189, 248, 0.6) !important;
        background-image: var(--vt-select-icon-hover, url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%23f1f5f9' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.85rem center !important;
        background-size: 8px 10px !important;
    }

    .aceternity-input:focus,
    .aceternity-select:focus {
        border-color: #38bdf8 !important;
        box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.25) !important;
        background-color: rgba(2, 6, 23, 0.9) !important;
    }

    .aceternity-select:focus {
        background-image: var(--vt-select-icon-focus, url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%2338bdf8' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.85rem center !important;
        background-size: 8px 10px !important;
    }

    .aceternity-input::placeholder {
        color: #64748b !important;
    }

        /* Platform Multi-Pick Grid & Chips */
    .platform-multipick-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.65rem;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
    }

    @media (min-width: 768px) {
        .platform-multipick-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.75rem;
        }
    }

    .platform-chip-badge-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0.6rem 0.75rem;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        color: #e2e8f0;
        font-weight: 600;
        font-size: 0.88rem;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        user-select: none;
        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        min-width: 0;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
    }

    @media (max-width: 575.98px) {
        .platform-multipick-grid {
            gap: 0.5rem;
        }
        .platform-chip-badge-card {
            padding: 0.5rem 0.45rem;
        }
        .platform-chip-badge-card .mr-2 {
            margin-right: 0.25rem !important;
        }
        .platform-chip-badge-card .mr-1 {
            margin-right: 0.15rem !important;
        }
        .platform-chip-badge-card .ml-1 {
            margin-left: 0.15rem !important;
        }
        .platform-chip-name {
            font-size: 0.70rem;
            letter-spacing: -0.015em;
            font-weight: 600;
        }
        .platform-chip-phase-badge {
            font-size: 0.65rem !important;
            padding: 0.18rem 0.45rem !important;
        }
    }

    .platform-chip-card-active {
        background: rgba(56, 189, 248, 0.12) !important;
        border-color: #38bdf8 !important;
        color: #ffffff !important;
        box-shadow: 0 0 20px -3px rgba(56, 189, 248, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
    }

    .platform-chip-card-roadmap {
        background: rgba(255, 255, 255, 0.02) !important;
        border: 1px dashed rgba(255, 255, 255, 0.15) !important;
        opacity: 0.72;
    }

    .platform-chip-status-text {
        font-size: 0.68rem;
        letter-spacing: 0.02em;
    }

    .platform-chip-upcoming {
        font-size: 0.95rem;
        opacity: 0.6;
    }

    .platform-chip-btn {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0.65rem 0.85rem;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        color: #e2e8f0;
        font-weight: 600;
        font-size: 0.88rem;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        user-select: none;
    }

    .platform-chip-phase-badge {
        font-size: 0.65rem;
        font-weight: 700;
        padding: 0.15rem 0.45rem;
        border-radius: 999px;
        letter-spacing: 0.03em;
        text-transform: uppercase;
    }

    .badge-phase-1 {
        background: rgba(16, 185, 129, 0.15);
        color: #34d399;
        border: 1px solid rgba(16, 185, 129, 0.35);
    }

    .badge-phase-2 {
        background: rgba(148, 163, 184, 0.12);
        color: #94a3b8;
        border: 1px solid rgba(148, 163, 184, 0.25);
    }

    .platform-chip-btn:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.25);
        color: #ffffff;
        transform: translateY(-1px);
    }

    .platform-chip-selected {
        background: rgba(56, 189, 248, 0.12) !important;
        border-color: #38bdf8 !important;
        color: #ffffff !important;
        box-shadow: 0 0 20px -3px rgba(56, 189, 248, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
    }

    .platform-chip-check {
        font-size: 1.1rem;
    }

    .platform-chip-disabled {
        opacity: 0.55;
        cursor: not-allowed !important;
        border-style: dashed !important;
    }

    .platform-chip-disabled:hover {
        transform: none !important;
        background: rgba(255, 255, 255, 0.04) !important;
        border-color: rgba(255, 255, 255, 0.1) !important;
    }

    .platform-chip-badge {
        font-size: 0.65rem;
        background-color: rgba(255, 255, 255, 0.1);
        color: #94a3b8;
        border: 1px solid rgba(255, 255, 255, 0.15);
        font-weight: 600;
        letter-spacing: 0.02em;
    }

    /* Testimonials Section */
    .landing-testimonials-section {
        background-color: #030712;
        border-top: 1px solid rgba(255, 255, 255, 0.06);
    }

    .testimonial-card {
        background: rgba(15, 23, 42, 0.7);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 16px;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
        transition: transform 0.25s ease, border-color 0.25s ease;
    }

    .testimonial-card:hover {
        transform: translateY(-3px);
        border-color: rgba(56, 189, 248, 0.3);
    }

    .rating-stars {
        font-size: 0.85rem;
        letter-spacing: 2px;
    }

    .badge-metric-emerald {
        background: rgba(16, 185, 129, 0.15);
        color: #34d399;
        border: 1px solid rgba(16, 185, 129, 0.3);
        font-size: 0.70rem;
        font-weight: 700;
        padding: 0.35rem 0.65rem;
    }

    .badge-metric-cyan {
        background: rgba(56, 189, 248, 0.15);
        color: #38bdf8;
        border: 1px solid rgba(56, 189, 248, 0.3);
        font-size: 0.70rem;
        font-weight: 700;
        padding: 0.35rem 0.65rem;
    }

    .badge-metric-amber {
        background: rgba(245, 158, 11, 0.15);
        color: #fbbf24;
        border: 1px solid rgba(245, 158, 11, 0.3);
        font-size: 0.70rem;
        font-weight: 700;
        padding: 0.35rem 0.65rem;
    }

    .testimonial-quote {
        color: #e2e8f0;
        font-size: 0.95rem;
        line-height: 1.6;
        font-style: italic;
        margin-bottom: 1.5rem;
    }

    .border-top-dark {
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .author-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.85rem;
        letter-spacing: 0.5px;
        flex-shrink: 0;
    }

    .avatar-cyan {
        background: linear-gradient(135deg, #0284c7 0%, #38bdf8 100%);
        color: #ffffff;
    }

    .avatar-purple {
        background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
        color: #ffffff;
    }

    .avatar-amber {
        background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
        color: #ffffff;
    }

    .author-name {
        font-size: 0.92rem;
    }

    .waitlist-terms {
        color: #94a3b8;
    }

    /* Security Section */
    .landing-security-section {
        background-color: #030712;
        border-top: 1px solid rgba(255, 255, 255, 0.06);
    }

    .security-card {
        background: rgba(15, 23, 42, 0.7);
        border: 1px solid rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
        transition: transform 0.25s ease, border-color 0.25s ease;
    }

    .security-card:hover {
        transform: translateY(-3px);
        border-color: rgba(56, 189, 248, 0.35);
    }

    .security-card-header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 0.75rem;
    }

    .security-card-icon {
        font-size: 1.75rem;
        margin-bottom: 0.75rem;
    }

    .security-card-title {
        font-size: 1.15rem;
        margin-bottom: 0;
    }

    .security-trust-bar {
        background: rgba(15, 23, 42, 0.55);
        border: 1px solid rgba(56, 189, 248, 0.2);
        max-width: 900px;
    }

    /* Pricing Section */
    .landing-pricing-section {
        background-color: #030712;
        border-top: 1px solid rgba(255, 255, 255, 0.06);
    }

    .pricing-guarantee-ribbon {
        background: rgba(15, 23, 42, 0.65);
        border: 1px solid rgba(245, 158, 11, 0.3);
        box-shadow: 0 0 24px rgba(245, 158, 11, 0.08);
        max-width: 820px;
    }

    .pricing-tier-card {
        background: rgba(15, 23, 42, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
        transition: transform 0.25s ease, border-color 0.25s ease;
    }

    .pricing-tier-card:hover {
        transform: translateY(-3px);
        border-color: rgba(56, 189, 248, 0.4);
    }

    .pricing-tier-popular {
        border-color: rgba(56, 189, 248, 0.5);
        background: linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.85) 100%);
        box-shadow: 0 0 30px rgba(56, 189, 248, 0.15);
    }

    .pricing-price-main {
        font-size: 2.25rem;
        line-height: 1;
    }

    .pricing-price-period {
        font-size: 0.95rem;
    }

    .pricing-card-desc {
        min-height: 40px;
    }

    .btn-pricing-card {
        border-radius: 8px;
    }

    /* FAQ Section */
    .faq-item {
        border-radius: 8px;
        overflow: hidden;
    }

    .faq-chevron {
        transition: transform 0.2s ease-in-out;
    }

    .landing-footer {
        background-color: #030712;
        border-color: rgba(255, 255, 255, 0.08) !important;
        color: #94a3b8;
    }

    .landing-page-container,
    .landing-header,
    .landing-footer {
        background-color: #030712;
        color: #f1f5f9;
        border-color: rgba(255, 255, 255, 0.08) !important;
    }

    .landing-page-container .brand-name,
    .landing-page-container .text-dark {
        color: #f1f5f9 !important;
    }

    .landing-page-container .bg-surface,
    .landing-page-container .bg-light {
        background-color: #0f172a !important;
    }

    .landing-page-container .feature-card,
    .landing-page-container .faq-item,
    .landing-page-container .comparison-table {
        background-color: #0f172a !important;
        border-color: #1e293b !important;
        color: #f1f5f9;
    }

    .landing-page-container .faq-item .btn {
        color: #f1f5f9 !important;
        background-color: transparent !important;
        box-shadow: none !important;
    }

    .landing-page-container .comparison-table th,
    .landing-page-container .comparison-table td {
        vertical-align: middle !important;
        border-color: #1e293b;
        padding-top: 0.85rem !important;
        padding-bottom: 0.85rem !important;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
    }

    .landing-page-container .comparison-table th:first-child,
    .landing-page-container .comparison-table td:first-child {
        padding-left: 1.25rem !important;
        padding-right: 1rem !important;
    }

    .landing-page-container .preview-metric-box {
        background-color: #020617;
        border-color: #1e293b;
    }

    .landing-page-container .table-highlight {
        background-color: rgba(56, 189, 248, 0.1) !important;
    }

    ::v-deep .demo-modal-dark {
        background-color: #0f172a !important;
        border: 1px solid rgba(255, 255, 255, 0.15) !important;
        border-radius: 12px;
        color: #f1f5f9;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6) !important;
    }

    ::v-deep .demo-modal-dark .modal-header {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        color: #f1f5f9;
    }

    ::v-deep .demo-modal-dark .modal-title {
        color: #f1f5f9;
        font-weight: 700;
    }

    ::v-deep .demo-modal-dark .close {
        color: #94a3b8;
        opacity: 0.8;
    }

    ::v-deep .demo-modal-dark .close:hover {
        color: #fff;
        opacity: 1;
    }

    ::v-deep .demo-modal-dark .modal-body {
        background-color: #0f172a;
    }

    ::v-deep .demo-modal-dark video::cue {
        font-size: 0.95rem;
        line-height: 1.4;
        background-color: rgba(15, 23, 42, 0.85);
        color: #f8fafc;
        border-radius: 4px;
        padding: 2px 6px;
    }

    ::v-deep .demo-modal-dark .form-control {
        background-color: #1e293b;
        border-color: rgba(255, 255, 255, 0.15);
        color: #fff;
    }

    ::v-deep .demo-modal-dark .form-control:focus {
        background-color: #1e293b;
        border-color: #38bdf8;
        color: #fff;
        box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.25);
    }

    @media (max-width: 575.98px) {
        ::v-deep .demo-modal-dark .input-group-text {
            font-size: 0.78rem;
            padding: 0.375rem 0.5rem;
        }
    }

    /* Sticky Header Scrolled State - Thin Sleek Navigation Bar */
    .landing-header-scrolled {
        background-color: rgba(3, 7, 18, 0.94) !important;
        backdrop-filter: blur(20px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.85), 0 1px 0 rgba(56, 189, 248, 0.2);
        border-bottom-color: rgba(56, 189, 248, 0.2) !important;
        padding-top: 0.35rem !important;
        padding-bottom: 0.35rem !important;
    }
    .landing-header-scrolled .brand-logo-img {
        width: 26px;
        height: 26px;
    }
    .landing-header-scrolled .brand-name {
        font-size: 1.1rem;
    }
    .landing-header-scrolled .landing-nav-link {
        font-size: 0.8rem;
        padding: 0.2rem 0.55rem;
    }
    .landing-header-scrolled .header-btn {
        padding-top: 0.2rem !important;
        padding-bottom: 0.2rem !important;
        font-size: 0.78rem !important;
    }

    .persona-custom-link {
        transition: color 0.2s ease;
    }
    .persona-custom-link:hover {
        color: #38bdf8 !important;
        text-decoration: underline !important;
    }

    .platform-badge-link {
        transition: transform 0.2s ease, filter 0.2s ease;
        display: inline-block;
        border-radius: 6px;
    }
    .platform-badge-link:hover {
        transform: translateY(-2px);
        filter: brightness(1.08);
    }

    /* DevHunt Community Contest Ribbon */
    .devhunt-contest-banner {
        background: rgba(249, 115, 22, 0.08);
        border: 1px solid rgba(249, 115, 22, 0.3);
        border-radius: 9999px;
        font-size: 0.82rem;
        line-height: 1.4;
        transition: all 0.25s ease;
        backdrop-filter: blur(8px);
    }
    .devhunt-contest-banner:hover {
        border-color: rgba(249, 115, 22, 0.5);
        background: rgba(249, 115, 22, 0.14);
        box-shadow: 0 0 16px rgba(249, 115, 22, 0.2);
    }
    .devhunt-contest-banner .contest-text {
        color: #cbd5e1;
    }
    .devhunt-contest-banner .devhunt-vote-btn {
        font-size: 0.74rem;
        font-weight: 600;
        padding: 0.15rem 0.65rem;
        border-radius: 9999px;
        background: rgba(249, 115, 22, 0.18);
        border: 1px solid rgba(249, 115, 22, 0.45);
        color: #fb923c;
        text-decoration: none;
        transition: all 0.2s ease;
        white-space: nowrap;
    }
    .devhunt-contest-banner .devhunt-vote-btn:hover {
        background: #f97316;
        border-color: #f97316;
        color: #ffffff;
        text-decoration: none;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(249, 115, 22, 0.35);
    }

    /* Hero Cockpit Window Frame */
    .hero-cockpit-wrapper {
        max-width: 1080px;
        position: relative;
    }

    .cockpit-window-frame {
        background: #0f172a;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 50px -10px rgba(56, 189, 248, 0.15);
        backdrop-filter: blur(16px);
    }

    .cockpit-titlebar {
        background: #090d16;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .window-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        display: inline-block;
    }

    .dot-red { background: #ef4444; }
    .dot-yellow { background: #f59e0b; }
    .dot-green { background: #10b981; }

    .cockpit-tab-pills {
        gap: 4px;
    }

    .cockpit-tab-pill {
        background: transparent;
        border: 1px solid transparent;
        color: #94a3b8;
        font-size: 0.78rem;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .cockpit-tab-pill:hover {
        color: #f1f5f9;
        background: rgba(255, 255, 255, 0.05);
    }

    .cockpit-tab-pill.active {
        color: #38bdf8;
        background: rgba(56, 189, 248, 0.12);
        border-color: rgba(56, 189, 248, 0.3);
    }

    .cockpit-body {
        background: #0b1120;
        min-height: 200px;
    }

    .cockpit-screenshot-wrapper {
        position: relative;
        background: #090d16;
    }

    .cockpit-screenshot-frame {
        width: 100%;
        height: 640px;
        overflow: hidden;
        position: relative;
        background: #0b1120;
    }

    .cockpit-screenshot {
        width: 100%;
        height: auto;
        display: block;
    }

    @media (max-width: 991.98px) {
        .cockpit-screenshot-frame {
            height: 450px;
        }
    }

    @media (max-width: 767.98px) {
        .cockpit-screenshot-frame {
            height: 280px;
        }
    }

    .cockpit-screenshot-badge-bar {
        background: #090d16;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }    .feature-point-row {
        font-size: 0.92rem;
        line-height: 1.5;
        color: #cbd5e1 !important;
    }

    .feature-action-bar {
        border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
    }

    .cockpit-footer-ribbon {
        background: #090d16;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    /* ROI Calculator Section */
    .landing-calculator-section {
        background-color: #030712;
        background-image: radial-gradient(circle at 50% 50%, rgba(56, 189, 248, 0.06), transparent 70%);
    }

    .calculator-card {
        background: rgba(15, 23, 42, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(16px);
    }

    .border-right-dark {
        border-right: 1px solid rgba(255, 255, 255, 0.08);
    }

    @media (max-width: 767.98px) {
        .border-right-dark {
            border-right: none;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            padding-bottom: 1.5rem;
            margin-bottom: 1.5rem;
        }
    }

    .aceternity-slider {
        accent-color: #38bdf8;
        cursor: pointer;
    }

    .roi-metric-box {
        background: rgba(16, 185, 129, 0.08);
        border: 1px solid rgba(16, 185, 129, 0.25);
    }

    .roi-submetrics-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
        width: 100%;
    }

    .roi-submetric-box {
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        text-align: center;
        height: 100%;
        min-width: 0;
        box-sizing: border-box;
    }

    .roi-submetric-label {
        min-height: 2.2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1.25;
        font-size: 0.82rem;
        text-align: center;
    }

    .roi-submetric-subtext {
        white-space: nowrap;
    }

    .roi-cta-btn {
        padding-top: 0.65rem !important;
        padding-bottom: 0.65rem !important;
    }

    /* Mid-Page Conversion Section */
    .landing-midpage-cta-section {
        background-color: #030712;
        padding-top: 3rem;
        padding-bottom: 3rem;
    }

    .midpage-cta-card {
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.9) 100%);
        border: 1px solid rgba(56, 189, 248, 0.25);
        box-shadow: 0 0 50px -10px rgba(56, 189, 248, 0.2);
    }

    .comparison-table-responsive {
        border-radius: 10px;
        border: 1px solid #1e293b;
        -webkit-overflow-scrolling: touch;
    }

    .comparison-swipe-hint {
        color: #94a3b8;
        font-size: 0.78rem;
    }

    .landing-footer-links {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
    }

    /* ==========================================================================
       TABLET & MOBILE RESPONSIVENESS (Breakpoints: 992px, 768px and 576px / 480px)
       ========================================================================== */
    @media (min-width: 768px) and (max-width: 991.98px) {
        .landing-page .container {
            max-width: 100%;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
        }
        .cockpit-footer-ribbon {
            justify-content: center !important;
            gap: 0.35rem 1.25rem;
            text-align: center;
        }
    }

    @media (max-width: 767.98px) {
        /* Section Spacings on Mobile */
        .landing-hero {
            padding: 3rem 0.75rem 2.25rem 0.75rem !important;
        }
        .landing-capabilities-section {
            padding: 3rem 0.75rem 2rem 0.75rem !important;
        }
        .landing-calculator-section,
        .landing-testimonials-section,
        .landing-waitlist-section,
        .landing-launch-section {
            padding: 3rem 0.75rem !important;
        }
        .landing-footer {
            padding-top: 2.5rem !important;
            padding-bottom: 2.5rem !important;
        }

        /* Hero Typography & CTA Buttons */
        .hero-subtitle {
            font-size: 0.98rem;
            line-height: 1.55;
            margin-bottom: 1.5rem !important;
        }
        .hero-cta-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.75rem 1.25rem !important;
            font-size: 0.95rem;
            min-height: 48px;
        }
        .hero-video-frame-btn.hero-cta-btn {
            padding: 0.4rem 0.85rem !important;
        }
        .hero-cta-group {
            width: 100%;
            max-width: 380px;
            margin-left: auto;
            margin-right: auto;
        }

        /* Cockpit Window & Touch Tab Strip */
        .cockpit-titlebar {
            padding: 0.5rem 0.75rem !important;
        }
        .cockpit-tab-pills {
            overflow-x: auto;
            white-space: nowrap;
            flex-wrap: nowrap;
            scrollbar-width: none;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 2px;
            max-width: calc(100% - 60px);
        }
        .cockpit-tab-pills::-webkit-scrollbar {
            display: none;
        }
        .cockpit-tab-pill {
            padding: 4px 8px;
            font-size: 0.74rem;
            flex-shrink: 0;
        }

                /* Security Cards Mobile Compacting: Icon & Title on Same Line */
        #security .col-md-6 {
            margin-bottom: 0.85rem !important;
        }
        .security-card {
            padding: 1.15rem 1rem !important;
            border-radius: 14px;
        }
        .security-card-header {
            flex-direction: row;
            align-items: center;
            margin-bottom: 0.55rem;
        }
        .security-card-icon {
            font-size: 1.35rem;
            margin-bottom: 0;
            margin-right: 0.75rem;
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .security-card-title {
            font-size: 1.05rem;
            line-height: 1.25;
            margin-bottom: 0;
        }
        .security-card p {
            font-size: 0.86rem;
            line-height: 1.5;
        }

        /* Capabilities & Features Spacing on Mobile */
        .landing-capabilities-section .text-center.mb-5,
        #features .text-center.mb-4,
        #features .text-center.mb-md-5 {
            margin-bottom: 1.5rem !important;
        }
        .landing-capabilities-section .capabilities-grid .col-lg-6 {
            margin-bottom: 0.85rem !important;
        }
        .capability-card {
            padding: 1.15rem 1rem !important;
            border-radius: 14px;
        }
        .capability-header {
            margin-bottom: 0.65rem;
        }
        .capability-icon-wrap {
            width: 38px;
            height: 38px;
            border-radius: 10px;
            margin-right: 0.75rem;
            margin-bottom: 0;
        }
        .capability-icon {
            font-size: 1.15rem;
        }
        .capability-title {
            font-size: 1.05rem;
            line-height: 1.25;
            margin-bottom: 0 !important;
        }
        .capability-description {
            font-size: 0.875rem;
            line-height: 1.5;
        }

        /* Feature Tabs: Responsive Wrap Pill Bar & Compact Text Detail */
        ::v-deep .feature-tabs {
            border-right: none !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            padding: 0.5rem 0.4rem !important;
            flex: 0 0 100% !important;
            max-width: 100% !important;
        }
        ::v-deep .feature-tabs .nav {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            gap: 0.3rem;
            padding-bottom: 0.2rem;
        }
        ::v-deep .feature-tabs .nav-item {
            flex: 0 0 auto;
        }
        ::v-deep .feature-tabs .nav-link {
            padding: 0.3rem 0.5rem !important;
            font-size: 0.76rem !important;
            white-space: nowrap;
            border-radius: 8px !important;
            display: inline-flex !important;
            align-items: center !important;
        }
        ::v-deep .feature-tabs .nav-link .py-2 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
        }
        ::v-deep .feature-tabs .nav-link small {
            display: none !important;
        }
        ::v-deep .feature-tabs .feature-tab-icon {
            font-size: 0.85rem !important;
            margin-right: 0.25rem !important;
        }
        ::v-deep .feature-card .tab-content {
            flex: 0 0 100% !important;
            max-width: 100% !important;
            padding: 1rem 0.85rem !important;
        }
        ::v-deep .feature-detail h4 {
            font-size: 1.1rem;
        }
        .feature-point-row {
            font-size: 0.84rem;
            margin-bottom: 0.35rem !important;
        }

        /* ROI Calculator Cards & Sliders */
        .calculator-card {
            padding: 1.25rem !important;
            border-radius: 14px;
        }
        .roi-metric-box {
            padding: 1rem !important;
        }
        .roi-metric-box .h2 {
            font-size: 1.75rem;
        }
        .roi-submetric-box {
            padding: 0.75rem !important;
        }
        .roi-submetric-box .h4 {
            font-size: 1.15rem;
        }
        .aceternity-slider {
            height: 38px;
        }

        /* Agile Comparison Table with Sticky Header Column */
        .comparison-table {
            min-width: 580px;
        }
        .comparison-table th:first-child,
        .comparison-table td:first-child {
            position: sticky;
            left: 0;
            z-index: 2;
            background-color: #0f172a !important;
            box-shadow: 2px 0 6px -2px rgba(0, 0, 0, 0.5);
            padding-left: 1rem !important;
        }

        /* Mid-Page CTA Card */
        .midpage-cta-card {
            padding: 1.5rem 1rem !important;
            border-radius: 14px;
        }
        .midpage-cta-card h2 {
            font-size: 1.35rem !important;
        }
        .midpage-cta-card .lead {
            font-size: 0.95rem !important;
        }

        /* Testimonials */
        .testimonial-card {
            padding: 1.25rem !important;
            margin-bottom: 1rem;
        }
        .testimonial-quote {
            font-size: 0.9rem;
            margin-bottom: 1rem;
        }

        /* Waitlist Card */
        .aceternity-card {
            border-radius: 14px !important;
        }
        ::v-deep .aceternity-card .card-body {
            padding: 1.25rem 1rem !important;
        }
        .waitlist-title {
            font-size: 1.45rem;
        }
        .waitlist-subtitle {
            font-size: 0.9rem;
        }
        .waitlist-capacity-box {
            padding: 0.85rem !important;
        }

        /* Footer Links */
        .landing-footer-links {
            justify-content: center !important;
            gap: 0.4rem 0.8rem;
            margin-top: 1rem;
        }
        .landing-footer-links .btn,
        .landing-footer-links a {
            font-size: 0.82rem !important;
            padding: 0.25rem 0.4rem !important;
        }
    }

    @media (max-width: 575.98px) {
        /* Extra-Small Header Adjustments */
        .landing-header {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
        }
        .brand-name {
            font-size: 1.05rem;
        }
        .brand-logo-img {
            width: 26px;
            height: 26px;
        }
        .header-btn {
            font-size: 0.78rem !important;
            padding: 0.35rem 0.55rem !important;
        }

        /* Cockpit Screenshot and Ribbon */
        .cockpit-screenshot-frame {
            height: 220px !important;
        }
        .cockpit-screenshot {
            height: 220px;
            object-fit: cover;
            object-position: top left;
        }
        .cockpit-footer-ribbon {
            flex-direction: column;
            gap: 0.4rem;
            text-align: center;
        }

        /* Waitlist Platform Chips: 2-column Grid */
        .platform-multipick-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 0.5rem;
        }
        .platform-chip-badge-card {
            padding: 0.5rem 0.45rem !important;
        }
        .platform-chip-name {
            font-size: 0.70rem !important;
            letter-spacing: -0.015em;
            font-weight: 600;
        }
        .platform-chip-phase-badge {
            font-size: 0.62rem !important;
            padding: 0.15rem 0.4rem !important;
        }

        /* ROI Calculator Mobile Adjustments */
        .calculator-card {
            padding: 1.15rem 1rem !important;
        }
        .roi-results-column {
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
        .roi-submetrics-grid {
            gap: 0.5rem;
        }
        .roi-submetric-box {
            padding: 0.65rem 0.45rem !important;
        }
        .roi-submetric-label {
            font-size: 0.74rem;
            min-height: 2rem;
            line-height: 1.2;
        }
        .roi-submetric-subtext {
            font-size: 0.72rem;
            white-space: nowrap;
        }
        .roi-cta-btn {
            font-size: 0.94rem !important;
            padding: 0.6rem 0.5rem !important;
            line-height: 1.3 !important;
        }

        /* FAQ Accordions */
        .faq-item .btn {
            padding: 0.75rem 0.85rem !important;
            font-size: 0.92rem;
            line-height: 1.4;
        }
    }

    .brand-scroll-top-btn {
        cursor: pointer;
        transition: opacity 0.18s ease, transform 0.18s ease;
    }
    .brand-scroll-top-btn:hover {
        opacity: 0.92;
        text-decoration: none;
    }

    .btn-back-to-top-float {
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 1040;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 1px solid rgba(56, 189, 248, 0.45);
        background: rgba(15, 23, 42, 0.9);
        color: #38bdf8;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1.15rem;
        box-shadow: 0 10px 25px rgba(2, 6, 23, 0.55), 0 0 14px rgba(56, 189, 248, 0.22);
        backdrop-filter: blur(8px);
        cursor: pointer;
        transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .btn-back-to-top-float:hover,
    .btn-back-to-top-float:focus {
        transform: translateY(-3px);
        background: rgba(14, 116, 144, 0.95);
        border-color: rgba(125, 211, 252, 0.85);
        color: #ffffff;
        box-shadow: 0 14px 28px rgba(2, 6, 23, 0.65), 0 0 18px rgba(56, 189, 248, 0.4);
        outline: none;
    }
</style>



